import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useCRM } from '../utils/CRMContext';
import { useAuth } from '../utils/AuthContext';
import { canAccessProposalAndClientPortal } from '../utils/userRoles';
import { useLeadTimeContext } from '../context/LeadTimeContext';
import { ClientPortal, PortalProject, PortalStage, LeadStatus, DesignSubStage, PortalNote, ProposalBuilderData, ProposalOpsReviewMeta, PortalStakeholderLink, PortalFileRelease, PortalReleaseBundle, EnhancedDeal } from '../types';
import { subscribePortalBundlesForDeal, subscribePortalReleasesForDeal } from '../utils/portalFileReleaseService';
import { staffBillableTotalFromProposal } from '../utils/proposalExclusiveEconomics';
import { lockDesignEstimating } from '../utils/proposalDesignBreakdown';
import { removeUndefinedDeep } from '../utils/removeUndefinedDeep';
import { syncProposalClientIdentityForDeals } from '../utils/proposalClientIdentitySync';
import { getSignedCommercialListTotal } from '../utils/proposalPublicGrandTotals';
import { db, storage } from '../utils/firebase';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, addDoc, setDoc, Timestamp, onSnapshot, deleteField, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { portalHostedAssetHref } from '../utils/portalAssetUrl';
import PortalModelVersionLoomPanel from '../components/portal/PortalModelVersionLoomPanel';
import PhaseManagementPanel from '../components/proposals/PhaseManagementPanel';
import PdfViewerModal from '../components/pdf/PdfViewerModal';
import ImageViewerModal from '../components/pdf/ImageViewerModal';
import CsvViewerModal from '../components/csv/CsvViewerModal';
import AppHeader from '../components/ui/AppHeader';
import LeadAgentWorkspace, { OPEN_AGENT_EVENT } from '../components/leads/LeadAgentWorkspace';
import { EXPAND_AGENT_COMMENTS_EVENT } from '../components/leads/LeadAgentCommentsStrip';
import { EXPAND_LEAD_COMMS_EVENT } from '../components/leads/LeadCommsSection';
import ClientWorkspaceSidebar from '../components/workspace/ClientWorkspaceSidebar';
import ClientWorkspaceHeader from '../components/workspace/ClientWorkspaceHeader';
import WorkspaceStageRail from '../components/workspace/WorkspaceStageRail';
import WorkspaceStageFooter from '../components/workspace/WorkspaceStageFooter';
import { computeNextBestActions, type NextBestAction } from '../utils/nextBestActions';
import { NameProjectFields, NameProjectFieldsValue, reserveProjectNumberOnSubmit } from '../components/projects/NameProjectFields';
import { NameProjectWizard } from '../components/projects/NameProjectWizard';
import {
  defaultClientLabelFromLead,
  defaultDescriptorFromProjectType,
  isProjectNumber,
  isLegacyDealName,
  getProjectDisplayName,
  parseLegacyTitle,
} from '../utils/projectName';
import { useJsApiLoader } from '@react-google-maps/api';
import {
  ArrowLeft,
  FileSignature,
  Box,
  FileText,
  ClipboardList,
  PenTool,
  Factory,
  Truck,
  Lock,
  X,
  Plus,
  RefreshCw,
  Mail,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  Eye,
  Upload,
  Loader2,
  FileEdit,
  StickyNote,
  Download,
  CheckCircle2,
  Clock,
  AlertCircle,
  AlertTriangle,
  Save,
  PenLine,
  MessageSquare,
  User,
  ImageIcon,
  Trash2,
  Building2,
  Send,
  Unlock,
  Activity,
  Pencil,
  Video,
  ExternalLink,
  ChevronDown,
  ShieldCheck,
  RotateCcw,
  Settings2,
  LayoutGrid,
} from 'lucide-react';
import { TransferDealToLeadModal } from '../components/deals/TransferDealToLeadModal';
import ProposalReviewModal from '../components/proposals/ProposalReviewModal';
import ProposalActivityTracker from '../components/proposals/ProposalActivityTracker';
import PublicProposalSettingsModal, { type TLPhase as PpsTLPhase } from '../components/proposals/PublicProposalSettingsModal';
import { getSettings } from '../utils/appSettings';
import { generateSignedAgreementPdf } from '../utils/generateSignedAgreementPdf';
import {
  buildEffectivePortalProject,
  buildPortalProjectFromDeal,
  isMultiProjectPortal,
  reconcilePortalProjectsForLead,
  type LeadDealPortalSlice,
} from '../utils/portalProjectView';
import {
  computePortalStepStatuses,
  resolveDisplayStage,
  signalsFromPortalProject,
  type PortalStageSignals,
} from '../utils/portalStageEngine';
import {
  formatApprovalBadge,
  isConceptApprovalFlowEnabled,
  resendPortalDeliverableApprovalNotifications,
} from '../utils/portalDeliverableApprovalService';
import { projectNeedsNumberAssignment } from '../utils/portalDesignFeePaid';
import { createProposalVersion } from '../utils/proposalVersioning';
import { subscribeOpsReviewMeta } from '../utils/proposalOpsReview';
import PortalActivityTimeline from '../components/portal/PortalActivityTimeline';
import PortalBillingSection from '../components/portal/PortalBillingSection';
import PortalModelWalkthroughStaffPanel from '../components/portal/PortalModelWalkthroughStaffPanel';
import ClientWorkspaceHelpModal from '../components/leads/ClientWorkspaceHelpModal';
import { useClientWorkspaceTour } from '../components/leads/ClientWorkspaceTour';
import { correctLatestAgreementPortalActivityTimestamp } from '../utils/portalActivityLogger';
import {
  applyInternalClientDirectionSign,
  CLIENT_DIRECTION_SIGNATURE_LABEL,
  hasClientPortalSignature,
} from '../utils/internalProposalSign';
import {
  computeModelWalkthroughEffective,
  parseWalkthroughProjectOverride,
} from '../utils/portalWalkthroughFeature';
import '../components/portal/PortalModelWalkthroughRecorder.css';
import './ClientPortalManagement.css';
import './ClientWorkspace.css';
import './OperationsHub.css';
import '../components/pm/pm-shared.css';

/** Safe `yyyy-mm-dd` for `<input type="date" />` from Date, Firestore Timestamp, ISO string, etc. */
function toDateInputValue(value: unknown): string {
  if (value == null || value === '') return '';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString().split('T')[0];
  }
  if (typeof value === 'object' && value !== null && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    try {
      const d = (value as { toDate: () => Date }).toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';
    } catch {
      return '';
    }
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  }
  return '';
}

/** Parse `yyyy-mm-dd` from a date input as local noon (matches deal date pickers). */
function parseLocalDateInput(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso || '').trim());
  if (!m) return new Date(NaN);
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  return new Date(y, mo - 1, d, 12, 0, 0, 0);
}

// ═══════════════════════════════════════════
// TIMELINE STEP CONFIG — NEW ORDER
// ═══════════════════════════════════════════

interface TimelineStep {
  id: PortalStage;
  number: number;
  title: string;
  shortTitle: string;
  icon: React.ReactNode;
}

const TIMELINE_STEPS: TimelineStep[] = [
  { id: 'design-fee', number: 1, title: 'Concept Design Fee', shortTitle: 'Fee', icon: <FileSignature size={16} /> },
  { id: '3d-model', number: 2, title: '3D Model', shortTitle: 'Model', icon: <Box size={16} /> },
  { id: 'quote', number: 3, title: 'Your Quote', shortTitle: 'Quote', icon: <ClipboardList size={16} /> },
  { id: 'quote-signed', number: 4, title: 'Quote Signed', shortTitle: 'Signed', icon: <PenTool size={16} /> },
  { id: '2d-plans', number: 5, title: '2D Plans', shortTitle: 'Plans', icon: <FileText size={16} /> },
  { id: 'in-production', number: 6, title: 'In Production', shortTitle: 'Prod', icon: <Factory size={16} /> },
  { id: 'delivery', number: 7, title: 'Delivery & Install', shortTitle: 'Delivery', icon: <Truck size={16} /> },
];

const STAGE_ORDER: PortalStage[] = ['design-fee', '3d-model', 'quote', 'quote-signed', '2d-plans', 'in-production', 'delivery'];

function getProjectFromPortal(portal: ClientPortal | null, dealId?: string): PortalProject | null {
  if (!portal || !dealId) return null;
  if (portal.projects?.[dealId]) {
    return portal.projects[dealId];
  }
  if (isMultiProjectPortal(portal)) {
    return null;
  }
  if (portal.dealId === dealId || !portal.dealId) {
    return {
      dealId,
      projectName: '',
      proposalSlug: portal.proposalSlug,
      engagementLetterId: portal.engagementLetterId,
      engagementLetterSlug: portal.engagementLetterSlug,
      currentStage: portal.currentStage,
      stageHistory: portal.stageHistory,
      designFeeSkipped: portal.designFeeSkipped,
      designFeePaid: portal.designFeePaid,
      designFeePaidAt: portal.designFeePaidAt,
      modelUrl: portal.modelUrl,
      modelImageUrl: portal.modelImageUrl,
      modelLoomUrl: portal.modelLoomUrl,
      modelNotes: portal.modelNotes,
      modelStepSkipped: portal.modelStepSkipped,
      conceptApprovalFlowDisabled: portal.conceptApprovalFlowDisabled,
      plansUrl: portal.plansUrl,
      productionStatus: portal.productionStatus,
      estimatedCompletion: portal.estimatedCompletion,
      productionNotes: portal.productionNotes,
      deliveryDate: portal.deliveryDate,
      deliveryStatus: portal.deliveryStatus,
      completionNotes: portal.completionNotes,
      createdAt: portal.createdAt,
      updatedAt: portal.updatedAt,
    };
  }
  return null;
}

// ═══════════════════════════════════════════
// DESIGN SUB-STAGE HELPERS
// ═══════════════════════════════════════════

const SUB_STAGE_COLORS: Record<DesignSubStage, string> = {
  'payment-requested': '#F59E0B',
  'in-design': '#8B5CF6',
  'design-review': '#EC4899',
  'revisions': '#F97316',
  'design-complete': '#22C55E',
};

const SUB_STAGE_LABELS: Record<DesignSubStage, string> = {
  'payment-requested': 'Payment Requested',
  'in-design': 'In Design',
  'design-review': 'Design Review',
  'revisions': 'Revisions',
  'design-complete': 'Design Complete',
};

/**
 * Calculate business days remaining from a start date to a deadline N business days later.
 * Returns negative values if past deadline.
 */
const getBusinessDaysRemaining = (startDate: Date, businessDays: number): { remaining: number; deadline: Date } => {
  const deadline = new Date(startDate);
  let added = 0;
  while (added < businessDays) {
    deadline.setDate(deadline.getDate() + 1);
    const day = deadline.getDay();
    if (day !== 0 && day !== 6) added++;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadlineMidnight = new Date(deadline);
  deadlineMidnight.setHours(0, 0, 0, 0);

  let remaining = 0;
  const cursor = new Date(today);
  if (deadlineMidnight >= today) {
    while (cursor < deadlineMidnight) {
      cursor.setDate(cursor.getDate() + 1);
      if (cursor.getDay() !== 0 && cursor.getDay() !== 6) remaining++;
    }
  } else {
    while (cursor > deadlineMidnight) {
      if (cursor.getDay() !== 0 && cursor.getDay() !== 6) remaining--;
      cursor.setDate(cursor.getDate() - 1);
    }
  }
  return { remaining, deadline };
};

// ═══════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════

const ClientPortalManagement: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stepFromUrl = searchParams.get('step') as PortalStage | null;
  const walkthroughHighlightId = searchParams.get('walkthrough');
  const { leads, enhancedDeals, pipelines, timberProjects, updateEnhancedDeal, deleteEnhancedDeal, updateLead, createActivity, createProjectForLead, syncSignedDealSideEffects } = useCRM();
  const { user } = useAuth();
  const canEditPortalMilestoneDates = canAccessProposalAndClientPortal(user?.role, user?.designations);
  const { setActiveLead } = useLeadTimeContext();

  // Google Maps — must match libraries used in MapView.tsx
  const GMAP_LIBS: ("places" | "geometry")[] = useMemo(() => ['places', 'geometry'], []);
  const { isLoaded: mapsLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GMAP_LIBS,
  });
  // Portal state
  const [portal, setPortal] = useState<ClientPortal | null>(null);
  const [portalId, setPortalId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Stakeholder view-only links
  const [stakeholderLinks, setStakeholderLinks] = useState<Array<PortalStakeholderLink & { id: string }>>([]);
  const [newStakeholderName, setNewStakeholderName] = useState('');
  const [stakeholderBusy, setStakeholderBusy] = useState(false);
  const [copiedStakeholderId, setCopiedStakeholderId] = useState<string | null>(null);

  // Selected timeline step
  const [selectedStep, setSelectedStep] = useState<PortalStage>('design-fee');

  const [showClientWorkspaceHelp, setShowClientWorkspaceHelp] = useState(false);

  // PDF / image / CSV viewers (shared comment-pin model)
  const [pdfViewerDoc, setPdfViewerDoc] = useState<{ url: string; title: string; documentId: string; annotationId?: string; pageNumber?: number } | null>(null);
  const [imageViewerDoc, setImageViewerDoc] = useState<{
    url: string;
    title: string;
    documentId: string;
    mimeType?: string;
    annotationId?: string;
  } | null>(null);
  const [csvViewerDoc, setCsvViewerDoc] = useState<{
    url: string;
    title: string;
    documentId: string;
    annotationId?: string;
  } | null>(null);

  // Client info editing
  const [editingClientInfo, setEditingClientInfo] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editCompany, setEditCompany] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editProjectType, setEditProjectType] = useState('');
  const [editBudget, setEditBudget] = useState('');
  const [savingClientInfo, setSavingClientInfo] = useState(false);

  // Editable fields
  const [modelUrl, setModelUrl] = useState('');
  const [plansUrl, setPlansUrl] = useState('');
  const [designFeeSkipped, setDesignFeeSkipped] = useState(false);
  const [inspirationBoardPortalEnabled, setInspirationBoardPortalEnabled] = useState(false);
  const [modelStepSkipped, setModelStepSkipped] = useState(false);
  const [conceptApprovalFlowDisabled, setConceptApprovalFlowDisabled] = useState(false);
  const [designFeePaid, setDesignFeePaid] = useState(false);
  const [generatingAgreementPdf, setGeneratingAgreementPdf] = useState(false);
  const [generatingSignedContract, setGeneratingSignedContract] = useState(false);
  const [editingFeeServices, setEditingFeeServices] = useState(false);
  const [feeServicesLocal, setFeeServicesLocal] = useState<{ id: string; name: string; price: number; description: string; required?: boolean }[]>([]);
  const [savingFeeServices, setSavingFeeServices] = useState(false);
  const [adminContractSignedDate, setAdminContractSignedDate] = useState('');
  const [adminDesignFeePaidDate, setAdminDesignFeePaidDate] = useState('');
  const [savingAdminDesignFeeDate, setSavingAdminDesignFeeDate] = useState(false);
  const [savingAdminAgreementSignedDate, setSavingAdminAgreementSignedDate] = useState(false);
  const [globalFeeServices, setGlobalFeeServices] = useState<{ id: string; name: string; price: number; description: string; required?: boolean }[]>([
    { id: '3d-model', name: '3D Concept Model', price: 1300, description: 'The core of your design — a detailed 3D model of your frame that allows us to create a perfectly accurate quote', required: true },
    { id: '2d-elevations', name: '2D Elevation Drawings', price: 850, description: 'PDF elevation drawings for your project' },
    { id: 'floor-plan', name: 'Floor Plan', price: 750, description: 'PDF floor plan for your project' },
  ]);
  const [modelNotes, setModelNotes] = useState('');
  const [globalWalkthroughDefault, setGlobalWalkthroughDefault] = useState(false);
  const [walkthroughOverride, setWalkthroughOverride] = useState<'inherit' | 'on' | 'off'>('inherit');
  const [savingWalkthroughToggle, setSavingWalkthroughToggle] = useState(false);
  const [portalNotes, setPortalNotes] = useState<PortalNote[]>([]);
  const [productionStatus, setProductionStatus] = useState<'queued' | 'in-progress' | 'complete'>('queued');
  const [estimatedCompletion, setEstimatedCompletion] = useState('');
  const [productionNotes, setProductionNotes] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState<'scheduled' | 'in-transit' | 'delivered' | 'installed'>('scheduled');
  const [completionNotes, setCompletionNotes] = useState('');

  // File upload state
  const [uploadingModel, setUploadingModel] = useState(false);
  const [uploadingModelImage, setUploadingModelImage] = useState(false);
  const [resendingApprovalNotify, setResendingApprovalNotify] = useState<
    'concept' | 'model' | 'plans' | null
  >(null);
  const [uploadingPlans, setUploadingPlans] = useState(false);
  const [modelImageUrl, setModelImageUrl] = useState('');
  const modelFileRef = useRef<HTMLInputElement>(null);
  const modelImageFileRef = useRef<HTMLInputElement>(null);
  const plansFileRef = useRef<HTMLInputElement>(null);

  // Proposal builder data (fetched separately)
  const [proposalBuilderData, setProposalBuilderData] = useState<any>(null);
  const [opsReviewMeta, setOpsReviewMeta] = useState<ProposalOpsReviewMeta | null>(null);

  // Review & Send modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showActivityTracker, setShowActivityTracker] = useState(false);
  const [sendingProposal, setSendingProposal] = useState(false);
  const [proposalEmailMessage, setProposalEmailMessage] = useState('');
  const [proposalEmailRecipients, setProposalEmailRecipients] = useState<string[]>([]);
  const [proposalEmailInitialized, setProposalEmailInitialized] = useState(false);

  // Timeline overrides
  const DEFAULT_TL_PHASES = [
    { id: '2d-plans', label: 'Engineered Drawings', durationWeeks: 3, bufferWeeks: 1 },
    { id: 'in-production', label: 'Production', durationWeeks: 8, bufferWeeks: 1, subPhases: [
      { id: 'material-lead', label: 'Material Lead Time', durationWeeks: 6 },
      { id: 'cnc-production', label: 'CNC Production', durationWeeks: 2 },
    ] },
    { id: 'delivery', label: 'Delivery & Install', durationWeeks: 2, bufferWeeks: 0 },
  ];
  const [timelineOverrides, setTimelineOverrides] = useState<typeof DEFAULT_TL_PHASES>(DEFAULT_TL_PHASES);
  // Brief "Settings saved" flash on the Edit Settings button after a
  // successful write through the popup.
  const [timelineSaved, setTimelineSaved] = useState(false);
  // Unified Public Proposal Settings popup — owns its own draft state, just
  // open/close + a one-shot snapshot of current values are managed here.
  const [publicSettingsOpen, setPublicSettingsOpen] = useState(false);

  // Multi-project state
  const [activeDealId, setActiveDealId] = useState<string | null>(null);
  /** When true, auto-switch effect may not move off a hidden-from-portal tab (user opened it from Archived). */
  const userPickedArchivedTabRef = useRef(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [transferDealTarget, setTransferDealTarget] = useState<EnhancedDeal | null>(null);
  // Canonical project naming state for the Add Project flow.
  const [newProjectNaming, setNewProjectNaming] = useState<NameProjectFieldsValue>({
    projectNumber: '',
    clientLabel: '',
    descriptor: '',
  });
  const [newProjectError, setNewProjectError] = useState<string | null>(null);
  const [deferProjectNumber, setDeferProjectNumber] = useState(false);

  // Time tracking: set active lead when on this page
  useEffect(() => {
    if (leadId) {
      setActiveLead(leadId, activeDealId ?? undefined, undefined);
    } else {
      setActiveLead(null);
    }
    return () => setActiveLead(null);
  }, [leadId, activeDealId, setActiveLead]);
  const [addingProject, setAddingProject] = useState(false);
  const [renamingDealId, setRenamingDealId] = useState<string | null>(null);
  const [renamingAssignNumber, setRenamingAssignNumber] = useState(false);
  const [editingLeadName, setEditingLeadName] = useState(false);
  const [editFullName, setEditFullName] = useState('');

  // Find the lead
  const lead = useMemo(() => leads.find(l => l.id === leadId), [leads, leadId]);

  const { startTour: startClientWorkspaceTour } = useClientWorkspaceTour(!loading && !!lead);

  // Find ALL deals for this lead
  const relatedDeals = useMemo(() => {
    if (!lead?.id) return [];
    return enhancedDeals.filter(d => d.leadId === lead.id);
  }, [lead, enhancedDeals]);

  const { portalVisibleDeals, portalArchivedDeals } = useMemo(() => {
    if (!portal || relatedDeals.length === 0) {
      return { portalVisibleDeals: relatedDeals, portalArchivedDeals: [] as typeof relatedDeals };
    }
    const visible: typeof relatedDeals = [];
    const archived: typeof relatedDeals = [];
    for (const d of relatedDeals) {
      if (!d.id) continue;
      const p = getProjectFromPortal(portal, d.id);
      if (p?.hiddenFromPortal) archived.push(d);
      else visible.push(d);
    }
    return { portalVisibleDeals: visible, portalArchivedDeals: archived };
  }, [relatedDeals, portal]);

  useEffect(() => {
    userPickedArchivedTabRef.current = false;
  }, [leadId]);

  const dealFromUrl = searchParams.get('deal');

  // Honor ?deal= from Project Hub (etc.) when the URL or lead changes — not on every tab click.
  useEffect(() => {
    if (!dealFromUrl || relatedDeals.length === 0) return;
    if (relatedDeals.some(d => d.id === dealFromUrl)) {
      setActiveDealId(dealFromUrl);
    }
  }, [dealFromUrl, relatedDeals, leadId]);

  // Prefer first project that is not hidden from the client portal when picking a default tab.
  useEffect(() => {
    if (relatedDeals.length === 0) return;

    const inList = !!(activeDealId && relatedDeals.some(d => d.id === activeDealId));
    if (!inList) {
      const firstPreferred =
        portal
          ? relatedDeals.find(d => d.id && !getProjectFromPortal(portal, d.id)?.hiddenFromPortal) ?? relatedDeals[0]
          : relatedDeals[0];
      setActiveDealId(firstPreferred.id!);
      return;
    }

    if (!portal || portalVisibleDeals.length === 0) return;
    if (userPickedArchivedTabRef.current) return;
    const cur = activeDealId ? getProjectFromPortal(portal, activeDealId) : null;
    if (cur?.hiddenFromPortal) {
      setActiveDealId(portalVisibleDeals[0].id!);
    }
  }, [relatedDeals, activeDealId, portal, portalVisibleDeals]);

  const selectProjectTab = useCallback(
    (dealId: string, options?: { archivedRow?: boolean }) => {
      if (options?.archivedRow) userPickedArchivedTabRef.current = true;
      else userPickedArchivedTabRef.current = false;
      setActiveDealId(dealId);
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set('deal', dealId);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Active deal (selected project tab)
  const activeDeal = useMemo(() => {
    if (!activeDealId) return relatedDeals[0] || null;
    return relatedDeals.find(d => d.id === activeDealId) || null;
  }, [relatedDeals, activeDealId]);

  /** Prefer Under Contract Hub when both precon and contracted exist for the same deal. */
  const timberProjectForActiveDeal = useMemo(() => {
    if (!activeDeal?.id) return null;
    const matches = timberProjects.filter(tp => tp.dealId === activeDeal.id);
    if (matches.length === 0) return null;
    return matches.find(p => p.phase === 'contracted') ?? matches[0];
  }, [activeDeal?.id, timberProjects]);

  // Backward compat alias
  const relatedDeal = activeDeal;

  const quoteDisplayTotal = useMemo(() => {
    const signed = proposalBuilderData
      ? getSignedCommercialListTotal(proposalBuilderData as ProposalBuilderData)
      : null;
    if (signed != null) return signed;
    const projected = staffBillableTotalFromProposal(proposalBuilderData);
    if (projected > 0) return projected;
    return relatedDeal?.value || 0;
  }, [proposalBuilderData, relatedDeal?.value]);

  // Active project data from portal
  const activeProject = useMemo(() => {
    if (!portal || !activeDealId) return null;
    return getProjectFromPortal(portal, activeDealId);
  }, [portal, activeDealId]);

  const showAssignNumberBanner = useMemo(
    () => projectNeedsNumberAssignment(activeDeal, activeProject, {
      underContract: timberProjectForActiveDeal?.phase === 'contracted' || activeDeal?.outcome === 'won',
    }),
    [activeDeal, activeProject, timberProjectForActiveDeal?.phase],
  );

  /**
   * Released-from-Project-Hub history for the current deal — used to render the
   * "From Project Hub" provenance badge on the 3D Model and Engineered Drawings
   * cards. A small map by source release id makes the lookup O(1) at render time.
   */
  const [portalReleases, setPortalReleases] = useState<PortalFileRelease[]>([]);
  const [portalBundles, setPortalBundles] = useState<PortalReleaseBundle[]>([]);
  useEffect(() => {
    if (!portalId || !activeDealId) {
      setPortalReleases([]);
      setPortalBundles([]);
      return;
    }
    const unsubReleases = subscribePortalReleasesForDeal(portalId, activeDealId, setPortalReleases);
    const unsubBundles = subscribePortalBundlesForDeal(portalId, activeDealId, setPortalBundles);
    return () => {
      unsubReleases();
      unsubBundles();
    };
  }, [portalId, activeDealId]);
  const releasesByReleaseId = useMemo(() => {
    const map = new Map<string, PortalFileRelease>();
    for (const r of portalReleases) if (r.id) map.set(r.id, r);
    return map;
  }, [portalReleases]);
  const activeProjectAny = activeProject as (PortalProject & {
    modelSourceReleaseId?: string;
    modelSourceProjectId?: string;
    modelSourceVersionLabel?: string;
    plansSourceReleaseId?: string;
    plansSourceProjectId?: string;
    plansSourceVersionLabel?: string;
  }) | null;
  const modelRelease = activeProjectAny?.modelSourceReleaseId
    ? releasesByReleaseId.get(activeProjectAny.modelSourceReleaseId)
    : null;
  const plansRelease = activeProjectAny?.plansSourceReleaseId
    ? releasesByReleaseId.get(activeProjectAny.plansSourceReleaseId)
    : null;

  /** Latest Project Hub release when portal row fields were cleared but release history remains. */
  const latestModelRelease = useMemo(() => {
    const html = portalReleases.filter(
      r => r.streamKind === 'three_d_html' && r.dealId === activeDealId,
    );
    if (html.length === 0) return null;
    return html.reduce((best, r) =>
      (r.releaseVersionIndex ?? 0) > (best.releaseVersionIndex ?? 0) ? r : best,
    );
  }, [portalReleases, activeDealId]);
  const latestPlansRelease = useMemo(() => {
    const pdfs = portalReleases.filter(
      r =>
        r.streamKind === 'two_d_pdf' &&
        r.dealId === activeDealId &&
        (!r.portalTarget || r.portalTarget === 'engineered_drawings'),
    );
    if (pdfs.length === 0) return null;
    return pdfs.reduce((best, r) =>
      (r.releaseVersionIndex ?? 0) > (best.releaseVersionIndex ?? 0) ? r : best,
    );
  }, [portalReleases]);
  const displayModelRelease = modelRelease ?? latestModelRelease;
  const displayPlansRelease = plansRelease ?? latestPlansRelease;
  const effectiveModelUrl = (modelUrl || displayModelRelease?.releasedUrl || '').trim();
  const effectivePlansUrl = (plansUrl || displayPlansRelease?.releasedUrl || '').trim();
  const modelFromHubOnly = !modelUrl.trim() && !!displayModelRelease?.releasedUrl;
  const plansFromHubOnly = !plansUrl.trim() && !!displayPlansRelease?.releasedUrl;

  // Load timeline overrides from active project or global settings
  useEffect(() => {
    const loadTimeline = async () => {
      if (activeProject?.timelineOverrides?.length) {
        setTimelineOverrides(activeProject.timelineOverrides as typeof DEFAULT_TL_PHASES);
      } else {
        try {
          const settings = await getSettings();
          if (settings.projectTimelineDefaults?.phases?.length) {
            setTimelineOverrides(settings.projectTimelineDefaults.phases as typeof DEFAULT_TL_PHASES);
          } else {
            setTimelineOverrides(DEFAULT_TL_PHASES);
          }
        } catch {
          setTimelineOverrides(DEFAULT_TL_PHASES);
        }
      }
    };
    loadTimeline();
  }, [activeProject]);

  // Get pipeline stage name for active deal
  const dealStageName = useMemo(() => {
    if (!activeDeal) return null;
    const pipeline = pipelines.find(p => p.id === activeDeal.pipelineId);
    const stage = pipeline?.stages.find(s => s.id === activeDeal.stageId);
    return stage?.name || 'Unknown Stage';
  }, [activeDeal, pipelines]);

  /**
   * Stage engine signals for the active project — the single source of truth
   * for the stepper, sidebar chips, advance/retreat buttons, and step gating.
   */
  const stageSignals = useMemo((): PortalStageSignals => {
    if (!portal) return { storedStage: 'design-fee' };
    const dealId = activeDealId || portal.dealId;
    const merged = dealId ? buildEffectivePortalProject(portal, dealId) : portal;
    const modelRelease = dealId
      ? portalReleases.find(r => r.streamKind === 'three_d_html' && r.dealId === dealId)
      : undefined;
    return signalsFromPortalProject(merged as ClientPortal, {
      releasedModelUrl: modelRelease?.releasedUrl,
      proposalStatus: proposalBuilderData?.status,
      dealOutcome: activeDeal?.outcome,
    });
  }, [portal, activeDealId, activeDeal?.outcome, proposalBuilderData?.status, portalReleases]);

  /** Display stage: per-project, with bumps suppressed after a manual retreat. */
  const effectiveCurrentStage = useMemo(
    (): PortalStage => resolveDisplayStage(stageSignals),
    [stageSignals],
  );

  /** Per-step status consumed by every stage surface on this page. */
  const stepStatuses = useMemo(
    () => computePortalStepStatuses(stageSignals),
    [stageSignals],
  );

  /** Firestore portal stage only (no won/approved bump). Used to detect manual advance vs waiting on client. */
  const storedPortalStage = useMemo((): PortalStage => {
    if (!portal) return 'design-fee';
    return (activeProject?.currentStage || portal.currentStage) as PortalStage;
  }, [portal, activeProject]);

  // ═══════════════════════════════════════════
  // WORKSPACE SHELL HELPERS (sidebar / header / rail)
  // ═══════════════════════════════════════════

  /** Canonical `#number` + descriptor for a project row. */
  const getDealDisplay = useCallback((deal: EnhancedDeal): { number: string; descriptor: string } => {
    const num = (deal.projectNumber || '').trim();
    const number = num ? (num.startsWith('#') ? num : `#${num}`) : '';
    const descriptor = (
      (deal.descriptor || '').trim() ||
      parseLegacyTitle(deal.title || '').descriptor ||
      deal.projectName ||
      deal.title ||
      'Project'
    ).trim();
    return { number, descriptor };
  }, []);

  /** Sidebar stage chip — same engine as the rail, per project. */
  const getSidebarDealStage = useCallback(
    (deal: EnhancedDeal): PortalStage | null => {
      if (!portal || !deal.id) return null;
      if (!getProjectFromPortal(portal, deal.id)) return null;
      return resolveDisplayStage(
        signalsFromPortalProject(buildEffectivePortalProject(portal, deal.id) as ClientPortal, {
          dealOutcome: deal.outcome,
          proposalStatus: deal.id === activeDealId ? proposalBuilderData?.status : undefined,
        }),
      );
    },
    [portal, activeDealId, proposalBuilderData?.status],
  );

  /** Always-visible active project label for the stage rail. */
  const activeProjectLabel = useMemo(() => {
    if (!activeDeal) {
      const name = lead ? `${lead.firstName} ${lead.lastName}`.trim() : '';
      return name || 'No project selected';
    }
    const { number, descriptor } = getDealDisplay(activeDeal);
    return [number, descriptor].filter(Boolean).join(' · ');
  }, [activeDeal, lead, getDealDisplay]);

  /** Rule-based next-best-action suggestions (v1) for the strip under the rail. */
  const nextBestActions = useMemo(() => {
    if (!portal || !activeDeal) return [];
    return computeNextBestActions({
      signals: stageSignals,
      displayStage: effectiveCurrentStage,
      proposalStatus: proposalBuilderData?.status,
      needsProjectNumber: showAssignNumberBanner,
      hasPortal: !!portal,
    });
  }, [portal, activeDeal, stageSignals, effectiveCurrentStage, proposalBuilderData?.status, showAssignNumberBanner]);

  const handleNextBestAction = useCallback(
    (action: NextBestAction) => {
      switch (action.id) {
        case 'assign-project-number':
          if (activeDeal) handleStartRename(activeDeal, { assignNumber: true });
          break;
        case 'send-portal-link':
          handleCopyLink();
          break;
        case 'start-3d-model':
          handleStageChange('3d-model');
          break;
        case 'build-quote':
          setSelectedStep('quote');
          break;
        case 'send-proposal':
          if (relatedDeal?.id) navigate(`/proposal-builder/${relatedDeal.id}`);
          else setSelectedStep('quote');
          break;
        case 'follow-up-proposal':
          setShowActivityTracker(true);
          break;
        case 'mark-signed':
          handleStageChange('quote-signed');
          break;
        case 'start-2d-plans':
          handleStageChange('2d-plans');
          break;
        case 'stage-pinned':
          if (action.stage) setSelectedStep(action.stage);
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeDeal, relatedDeal?.id, navigate],
  );

  /** Sidebar nav: scroll to the documents / team-notes sections, open agent panels. */
  const workspaceDocsRef = useRef<HTMLDivElement | null>(null);
  const scrollToDocuments = useCallback(() => {
    workspaceDocsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const focusTeamNotes = useCallback(() => {
    window.dispatchEvent(new Event(EXPAND_AGENT_COMMENTS_EVENT));
    requestAnimationFrame(() => {
      workspaceDocsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, []);
  const focusLeadComms = useCallback(() => {
    window.dispatchEvent(new Event(EXPAND_LEAD_COMMS_EVENT));
    requestAnimationFrame(() => {
      document.querySelector('[data-tour="cw-comms"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);
  const openAgentPanel = useCallback((kind: 'bidding' | 'marketing') => {
    window.dispatchEvent(new CustomEvent(OPEN_AGENT_EVENT, { detail: kind }));
  }, []);

  const teamNoteCommentId = (searchParams.get('comment') || '').trim();
  const openTeamNotesFromUrl = searchParams.get('comms') === 'comments' || !!teamNoteCommentId;
  const clearTeamNoteDeepLink = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const hadComment = next.has('comment');
      const hadComms = next.get('comms') === 'comments';
      if (!hadComment && !hadComms) return prev;
      next.delete('comment');
      if (hadComms) next.delete('comms');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (loading || !lead || !openTeamNotesFromUrl) return;
    window.dispatchEvent(new Event(EXPAND_AGENT_COMMENTS_EVENT));
    if (teamNoteCommentId) return;
    const frame = requestAnimationFrame(() => {
      workspaceDocsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
    clearTeamNoteDeepLink();
    return () => cancelAnimationFrame(frame);
  }, [loading, lead, openTeamNotesFromUrl, teamNoteCommentId, clearTeamNoteDeepLink]);

  // Proposals are tracked via proposalBuilderData (fetched per-deal) rather than the global proposals list

  // ═══════════════════════════════════════════
  // DATA FETCHING
  // ═══════════════════════════════════════════

  // Drop previous lead's portal immediately — fetchPortal is async and used to leave
  // stale slug/PIN visible when the new lead has no clientPortals doc yet.
  useEffect(() => {
    setPortal(null);
    setPortalId(null);
    setPortalNotes([]);
    setStakeholderLinks([]);
    setNewStakeholderName('');
    setCopiedStakeholderId(null);
    setActiveDealId(null);
    setProposalBuilderData(null);
    setOpsReviewMeta(null);
    setLoading(true);
  }, [leadId]);

  // Fetch portal data
  useEffect(() => {
    const fetchPortal = async () => {
      if (!leadId) { setLoading(false); return; }
      try {
        const portalsRef = collection(db, 'clientPortals');
        const q = query(portalsRef, where('leadId', '==', leadId));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const portalDoc = querySnapshot.docs[0];
          const data = portalDoc.data();

          // Parse projects map from Firestore
          let projects: { [dealId: string]: PortalProject } | undefined;
          if (data.projects && typeof data.projects === 'object') {
            projects = {};
            for (const [dId, pData] of Object.entries(data.projects as Record<string, any>)) {
              projects[dId] = {
                dealId: pData.dealId || dId,
                projectName: pData.projectName || '',
                proposalSlug: pData.proposalSlug,
                engagementLetterId: pData.engagementLetterId,
                engagementLetterSlug: pData.engagementLetterSlug,
                currentStage: pData.currentStage || 'design-fee',
                stageHistory: pData.stageHistory || [],
                designFeeSkipped: pData.designFeeSkipped || false,
                designFeePaid: pData.designFeePaid || false,
                designFeePaidAt: pData.designFeePaidAt?.toDate?.() || undefined,
                amountPaid: pData.amountPaid || undefined,
                selectedServices: pData.selectedServices || undefined,
                agreementSigned: pData.agreementSigned || false,
                agreementSignedAt: pData.agreementSignedAt?.toDate?.() || undefined,
                agreementSignedBy: pData.agreementSignedBy || undefined,
                agreementSignature: pData.agreementSignature || undefined,
                agreementServices: pData.agreementServices || undefined,
                agreementTotal: pData.agreementTotal || undefined,
                stripeSessionId: pData.stripeSessionId || undefined,
                stripeReceiptUrl: pData.stripeReceiptUrl || undefined,
                modelUrl: pData.modelUrl,
                modelImageUrl: pData.modelImageUrl,
                modelLoomUrl: pData.modelLoomUrl,
                modelNotes: pData.modelNotes,
                modelStepSkipped: pData.modelStepSkipped || false,
                conceptApprovalFlowDisabled: !!pData.conceptApprovalFlowDisabled,
                plansUrl: pData.plansUrl,
                productionStatus: pData.productionStatus,
                estimatedCompletion:
                  pData.estimatedCompletion?.toDate?.() ??
                  (typeof pData.estimatedCompletion === 'string' ? new Date(pData.estimatedCompletion) : undefined),
                productionNotes: pData.productionNotes,
                deliveryDate:
                  pData.deliveryDate?.toDate?.() ??
                  (typeof pData.deliveryDate === 'string' ? new Date(pData.deliveryDate) : undefined),
                deliveryStatus: pData.deliveryStatus,
                completionNotes: pData.completionNotes,
                timelineOverrides: pData.timelineOverrides,
                targetCompletionDate: pData.targetCompletionDate?.toDate?.() || undefined,
                questionnaireStatus: pData.questionnaireStatus || undefined,
                customDesignFeeServices: pData.customDesignFeeServices || undefined,
                modelWalkthroughEnabled:
                  typeof pData.modelWalkthroughEnabled === 'boolean' ? pData.modelWalkthroughEnabled : undefined,
                modelWalkthroughEffective:
                  typeof pData.modelWalkthroughEffective === 'boolean' ? pData.modelWalkthroughEffective : undefined,
                inspirationBoardPortalEnabled:
                  typeof pData.inspirationBoardPortalEnabled === 'boolean'
                    ? pData.inspirationBoardPortalEnabled
                    : undefined,
                createdAt: pData.createdAt?.toDate?.() || new Date(),
                updatedAt: pData.updatedAt?.toDate?.() || new Date(),
                hiddenFromPortal: pData.hiddenFromPortal || false,
              };
            }
          }

          const portalData: ClientPortal = {
            id: portalDoc.id,
            slug: data.slug,
            accessPin: data.accessPin,
            clientName: data.clientName,
            clientEmail: data.clientEmail,
            clientCompany: data.clientCompany || '',
            projects,
            leadId: data.leadId,
            dealId: data.dealId,
            engagementLetterId: data.engagementLetterId,
            engagementLetterSlug: data.engagementLetterSlug,
            proposalId: data.proposalId,
            proposalSlug: data.proposalSlug,
            currentStage: data.currentStage || 'design-fee',
            stageHistory: data.stageHistory || [],
            designFeeSkipped: data.designFeeSkipped || false,
            modelStepSkipped: data.modelStepSkipped || false,
            conceptApprovalFlowDisabled: !!data.conceptApprovalFlowDisabled,
            inspirationBoardPortalEnabled:
              typeof data.inspirationBoardPortalEnabled === 'boolean'
                ? data.inspirationBoardPortalEnabled
                : undefined,
            modelUrl: data.modelUrl,
            modelImageUrl: data.modelImageUrl,
            modelLoomUrl: data.modelLoomUrl,
            modelNotes: data.modelNotes,
            plansUrl: data.plansUrl,
            productionStatus: data.productionStatus,
            estimatedCompletion:
              data.estimatedCompletion?.toDate?.() ??
              (typeof data.estimatedCompletion === 'string' ? new Date(data.estimatedCompletion) : undefined),
            productionNotes: data.productionNotes,
            deliveryDate:
              data.deliveryDate?.toDate?.() ??
              (typeof data.deliveryDate === 'string' ? new Date(data.deliveryDate) : undefined),
            deliveryStatus: data.deliveryStatus,
            completionNotes: data.completionNotes,
            documentUploads: data.documentUploads,
            designFeePaid: data.designFeePaid || false,
            designFeePaidAt: data.designFeePaidAt?.toDate?.() || undefined,
            agreementSigned: data.agreementSigned || false,
            agreementSignedAt: data.agreementSignedAt?.toDate?.() || undefined,
            agreementSignedBy: data.agreementSignedBy || undefined,
            agreementSignature: data.agreementSignature || undefined,
            agreementServices: data.agreementServices || undefined,
            agreementTotal: data.agreementTotal || undefined,
            amountPaid: data.amountPaid || undefined,
            stripeSessionId: data.stripeSessionId || undefined,
            stripeReceiptUrl: data.stripeReceiptUrl || undefined,
            portalBackgroundImage: data.portalBackgroundImage || undefined,
            portalOverlayColor: data.portalOverlayColor || undefined,
            portalOverlayOpacity: data.portalOverlayOpacity ?? undefined,
            clientUploads: (data.clientUploads || []).map((u: any) => ({
              ...u,
              uploadedAt: u.uploadedAt?.toDate?.() || new Date(u.uploadedAt),
            })),
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.() || new Date(),
            lastViewedAt: data.lastViewedAt?.toDate?.(),
          };
          setPortal(portalData);
          setPortalId(portalDoc.id);
        } else {
          setPortal(null);
          setPortalId(null);
        }
      } catch (err) {
        console.error('Error fetching portal:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPortal();
  }, [leadId]);

  // Load global design fee services from settings (one-time)
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'appSettings', 'designFeePricing'));
        if (snap.exists() && snap.data().services?.length) {
          setGlobalFeeServices(snap.data().services);
        }
      } catch { /* use defaults */ }
    })();
  }, []);

  // Sync portal.projects with enhancedDeals on this lead: add missing rows and prune stale keys.
  useEffect(() => {
    if (!portalId || !portal) return;
    if (relatedDeals.length === 0) return;

    const validIds = new Set(relatedDeals.map(d => d.id).filter(Boolean) as string[]);
    const existingIds = new Set(Object.keys(portal.projects || {}));
    const missing = relatedDeals.filter(d => d.id && !existingIds.has(d.id));
    const staleIds = Object.keys(portal.projects || {}).filter(id => !validIds.has(id));

    if (missing.length === 0 && staleIds.length === 0) return;

    console.log('[ProjectSync] reconcile portal projects', {
      add: missing.map(d => d.id),
      remove: staleIds,
    });

    const leadDealSlices: LeadDealPortalSlice[] = relatedDeals
      .filter((d): d is EnhancedDeal & { id: string } => !!d.id)
      .map(d => ({
        id: d.id,
        projectName: d.projectName,
        descriptor: d.descriptor,
        projectNumber: d.projectNumber,
        clientLabel: d.clientLabel,
        title: d.title,
      }));

    const syncProjects = async () => {
      try {
        const updates: Record<string, any> = { updatedAt: Timestamp.now() };
        const now = Timestamp.now();

        for (const id of staleIds) {
          updates[`projects.${id}`] = deleteField();
        }

        for (const deal of missing) {
          if (!deal.id) continue;
          const row = buildPortalProjectFromDeal({
            id: deal.id,
            projectName: deal.projectName,
            descriptor: deal.descriptor,
            projectNumber: deal.projectNumber,
            clientLabel: deal.clientLabel,
            title: deal.title,
          });
          updates[`projects.${deal.id}`] = {
            ...row,
            stageHistory: [{ stage: 'design-fee', enteredAt: now }],
            createdAt: now,
            updatedAt: now,
          };
        }

        await updateDoc(doc(db, 'clientPortals', portalId), updates);
        setPortal(prev => {
          if (!prev) return null;
          const reconciled = reconcilePortalProjectsForLead(prev.projects || {}, leadDealSlices);
          return { ...prev, projects: reconciled };
        });
      } catch (err) {
        console.error('Error syncing projects to portal:', err);
      }
    };
    syncProjects();
  }, [portalId, portal?.id, portal?.projects, relatedDeals]);

  // Load global walkthrough default from settings
  useEffect(() => {
    void getSettings().then(s => setGlobalWalkthroughDefault(!!s.modelWalkthroughDefaultEnabled));
  }, []);

  useEffect(() => {
    if (walkthroughHighlightId) setSelectedStep('3d-model');
  }, [walkthroughHighlightId]);

  // Sync per-project state when active project changes
  useEffect(() => {
    const proj = activeProject;
    if (!proj && portal) {
      // No project data — fall back to portal flat fields (legacy)
      setModelUrl(portal.modelUrl || '');
      setModelImageUrl(portal.modelImageUrl || '');
      setPlansUrl(portal.plansUrl || '');
      setDesignFeeSkipped(portal.designFeeSkipped);
      setInspirationBoardPortalEnabled(!!portal.inspirationBoardPortalEnabled);
      setModelStepSkipped(portal.modelStepSkipped || false);
      setConceptApprovalFlowDisabled(!!portal.conceptApprovalFlowDisabled);
      setDesignFeePaid(portal.designFeePaid || false);
      setModelNotes(portal.modelNotes || '');
      setProductionStatus(portal.productionStatus || 'queued');
      setEstimatedCompletion(toDateInputValue(portal.estimatedCompletion));
      setProductionNotes(portal.productionNotes || '');
      setDeliveryDate(toDateInputValue(portal.deliveryDate));
      setDeliveryStatus(portal.deliveryStatus || 'scheduled');
      setCompletionNotes(portal.completionNotes || '');
      setSelectedStep(stepFromUrl || portal.currentStage);
    } else if (proj) {
      setModelUrl(proj.modelUrl || '');
      setModelImageUrl(proj.modelImageUrl || '');
      setPlansUrl(proj.plansUrl || '');
      setDesignFeeSkipped(proj.designFeeSkipped);
      setInspirationBoardPortalEnabled(!!proj.inspirationBoardPortalEnabled);
      setModelStepSkipped(proj.modelStepSkipped || false);
      setConceptApprovalFlowDisabled(!!proj.conceptApprovalFlowDisabled);
      setDesignFeePaid(proj.designFeePaid || false);
      setModelNotes(proj.modelNotes || '');
      setWalkthroughOverride(parseWalkthroughProjectOverride(proj.modelWalkthroughEnabled));
      setProductionStatus(proj.productionStatus || 'queued');
      setEstimatedCompletion(toDateInputValue(proj.estimatedCompletion));
      setProductionNotes(proj.productionNotes || '');
      setDeliveryDate(toDateInputValue(proj.deliveryDate));
      setDeliveryStatus(proj.deliveryStatus || 'scheduled');
      setCompletionNotes(proj.completionNotes || '');
      setSelectedStep(stepFromUrl || proj.currentStage);
    } else if (!portal) {
      setModelUrl('');
      setModelImageUrl('');
      setPlansUrl('');
      setDesignFeeSkipped(false);
      setInspirationBoardPortalEnabled(false);
      setModelStepSkipped(false);
      setConceptApprovalFlowDisabled(false);
      setDesignFeePaid(false);
      setModelNotes('');
      setWalkthroughOverride('inherit');
      setProductionStatus('queued');
      setEstimatedCompletion('');
      setProductionNotes('');
      setDeliveryDate('');
      setDeliveryStatus('scheduled');
      setCompletionNotes('');
      setSelectedStep('design-fee');
    }
    setProposalEmailInitialized(false);
    setEditingFeeServices(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject, portal]);

  const modelReleasesForStep = useMemo(
    () =>
      portalReleases.filter(r => r.streamKind === 'three_d_html' && r.dealId === activeDealId),
    [portalReleases, activeDealId],
  );

  const refreshProposalBuilder = useCallback(async (dealId?: string | null) => {
    const id = dealId ?? activeDealId;
    if (!id) return;
    try {
      const snapshot = await getDoc(doc(db, 'proposalBuilders', id));
      if (snapshot.exists()) {
        setProposalBuilderData({ id: snapshot.id, ...snapshot.data() });
      } else {
        setProposalBuilderData(null);
      }
    } catch (err) {
      console.error('Error fetching proposal builder:', err);
    }
  }, [activeDealId]);

  // Fetch proposal builder data when active deal changes
  useEffect(() => {
    void refreshProposalBuilder();
  }, [refreshProposalBuilder]);

  // OPS review status for quote step banner
  useEffect(() => {
    if (!activeDealId) {
      setOpsReviewMeta(null);
      return;
    }
    return subscribeOpsReviewMeta(activeDealId, setOpsReviewMeta);
  }, [activeDealId]);

  // Stakeholder view-only links (portal-level)
  useEffect(() => {
    if (!portalId) {
      setStakeholderLinks([]);
      return;
    }
    const q = query(
      collection(db, 'portalStakeholderLinks'),
      where('portalId', '==', portalId),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setStakeholderLinks(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as PortalStakeholderLink) })),
        );
      },
      (err) => console.error('[Stakeholder links]', err),
    );
    return () => unsub();
  }, [portalId]);

  // Auto-sync proposalSlug to portal when proposal builder data is available
  useEffect(() => {
    if (!proposalBuilderData?.slug || !portal?.id || !activeDealId) return;
    // Guard against tab-switch races: stale builder data from the previous deal must not
    // overwrite a sibling project's portal row (e.g. aux cabin getting admin slug).
    if (proposalBuilderData.id !== activeDealId) return;
    const currentSlug = activeProject?.proposalSlug || portal.proposalSlug;
    if (currentSlug === proposalBuilderData.slug) return;
    const syncProposalSlug = async () => {
      try {
        const updates: Record<string, any> = {
          [`projects.${activeDealId}.proposalSlug`]: proposalBuilderData.slug,
          updatedAt: Timestamp.now(),
        };
        if (activeDealId === portal.dealId || !portal.dealId) {
          updates.proposalSlug = proposalBuilderData.slug;
        }
        await updateDoc(doc(db, 'clientPortals', portal.id!), updates);
        setPortal(prev => {
          if (!prev) return null;
          const updated = { ...prev };
          if (activeDealId === prev.dealId || !prev.dealId) {
            updated.proposalSlug = proposalBuilderData.slug;
          }
          if (updated.projects?.[activeDealId]) {
            updated.projects = { ...updated.projects };
            updated.projects[activeDealId] = { ...updated.projects[activeDealId], proposalSlug: proposalBuilderData.slug };
          }
          return updated;
        });
      } catch (err) {
        console.error('Error syncing proposalSlug to portal:', err);
      }
    };
    syncProposalSlug();
  }, [proposalBuilderData?.slug, portal?.id, activeDealId, activeProject?.proposalSlug]);

  // Initialize proposal email defaults once portal/lead data is available
  useEffect(() => {
    if (proposalEmailInitialized) return;
    const clientEmail = portal?.clientEmail || lead?.email;
    if (!clientEmail) return;
    const firstName = portal?.clientName?.split(' ')[0] || lead?.firstName || 'there';
    setProposalEmailRecipients([clientEmail]);
    setProposalEmailMessage(
      `Hi ${firstName},\n\nGood afternoon! Your project proposal is ready for review. You can view the full details, including pricing and scope, in your client portal.\n\nPlease don't hesitate to reach out if you have any questions.\n\nThank you!`
    );
    setProposalEmailInitialized(true);
  }, [portal?.clientEmail, portal?.clientName, lead?.email, lead?.firstName, proposalEmailInitialized]);

  // Fetch portal notes (client feedback)
  const portalDocId = portal?.id;
  useEffect(() => {
    if (!portalDocId) return;
    const fetchPortalNotes = async () => {
      try {
        const notesRef = collection(db, 'portalNotes');
        const q = query(notesRef, where('portalId', '==', portalDocId));
        const snap = await getDocs(q);
        const notes: PortalNote[] = snap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            portalId: data.portalId,
            step: data.step,
            authorType: data.authorType,
            authorName: data.authorName,
            message: data.message,
            createdAt: data.createdAt?.toDate?.() || new Date(),
          };
        });
        notes.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        setPortalNotes(notes);
      } catch (err) {
        console.error('Error fetching portal notes:', err);
      }
    };
    fetchPortalNotes();
  }, [portalDocId]);

  // ═══════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════

  const handleCreatePortal = async () => {
    if (!lead || !leadId) return;
    setSaving(true);
    try {
      const lastName = lead.lastName.toLowerCase().replace(/[^a-z0-9]/g, '');
      const portalKey = Math.floor(1000 + Math.random() * 9000).toString();
      const slug = `${lastName}-${portalKey}`;
      const accessPin = Math.floor(1000 + Math.random() * 9000).toString();

      // Initialize projects map if there's already a deal
      const initialProjects: Record<string, any> = {};
      if (activeDeal?.id) {
        initialProjects[activeDeal.id] = {
          dealId: activeDeal.id,
          projectName: activeDeal.projectName || activeDeal.title || '',
          currentStage: 'design-fee',
          stageHistory: [{ stage: 'design-fee', enteredAt: Timestamp.now() }],
          designFeeSkipped: false,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };
      }

      const newPortal: Omit<ClientPortal, 'id'> = {
        slug, accessPin,
        clientName: `${lead.firstName} ${lead.lastName}`,
        clientEmail: lead.email,
        clientCompany: lead.company || '',
        leadId,
        dealId: activeDeal?.id || '',
        currentStage: 'design-fee',
        stageHistory: [{ stage: 'design-fee', enteredAt: new Date() }],
        designFeeSkipped: false,
        modelStepSkipped: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      const portalDoc: Record<string, any> = {
        ...newPortal,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        stageHistory: [{ stage: 'design-fee', enteredAt: Timestamp.now() }],
      };
      if (Object.keys(initialProjects).length > 0) {
        portalDoc.projects = initialProjects;
      }
      const portalsRef = collection(db, 'clientPortals');
      const docRef = await addDoc(portalsRef, portalDoc);
      setPortal({ ...newPortal, id: docRef.id, projects: Object.keys(initialProjects).length > 0 ? initialProjects as any : undefined });
      setPortalId(docRef.id);
      setSelectedStep('design-fee');
    } catch (err) {
      console.error('Error creating portal:', err);
      alert('Failed to create portal');
    } finally {
      setSaving(false);
    }
  };

  const handleStageChange = async (newStage: PortalStage) => {
    if (!portalId || !portal) return;
    setSaving(true);
    try {
      const currentHistory = activeProject?.stageHistory || portal.stageHistory || [];
      const newHistory = [...currentHistory, { stage: newStage, enteredAt: new Date() }];
      const historyForFirestore = newHistory.map(h => ({
        ...h,
        enteredAt: h.enteredAt instanceof Date ? Timestamp.fromDate(h.enteredAt) : h.enteredAt,
      }));

      const firestoreUpdates: Record<string, any> = { updatedAt: Timestamp.now() };

      if (activeDealId) {
        firestoreUpdates[`projects.${activeDealId}.currentStage`] = newStage;
        firestoreUpdates[`projects.${activeDealId}.stageHistory`] = historyForFirestore;
        // Advancing re-enables auto-bumps after a manual retreat.
        firestoreUpdates[`projects.${activeDealId}.stageManualOverride`] = deleteField();
        firestoreUpdates[`projects.${activeDealId}.updatedAt`] = Timestamp.now();
      }

      const primaryDealId = portal.dealId;
      if (!activeDealId || activeDealId === primaryDealId) {
        firestoreUpdates.currentStage = newStage;
        firestoreUpdates.stageHistory = historyForFirestore;
        firestoreUpdates.stageManualOverride = deleteField();
      }

      await updateDoc(doc(db, 'clientPortals', portalId), firestoreUpdates);

      setPortal(prev => {
        if (!prev) return null;
        const updated = { ...prev };
        if (!activeDealId || activeDealId === primaryDealId) {
          updated.currentStage = newStage;
          updated.stageHistory = newHistory;
          updated.stageManualOverride = false;
        }
        if (activeDealId && updated.projects) {
          updated.projects = { ...updated.projects };
          updated.projects[activeDealId] = {
            ...(updated.projects[activeDealId] || {}),
            currentStage: newStage,
            stageHistory: newHistory,
            stageManualOverride: false,
            updatedAt: new Date(),
          } as PortalProject;
        }
        return updated;
      });

      const dealIdForSignedSync = activeDealId ?? portal?.dealId ?? null;
      if (newStage === 'quote-signed' && dealIdForSignedSync && user?.id) {
        try {
          const proposalSnap = await getDoc(doc(db, 'proposalBuilders', dealIdForSignedSync));
          const existingSignature = proposalSnap.exists()
            ? (proposalSnap.data().signature as { dataUrl?: string; signedBy?: string; source?: string } | undefined)
            : undefined;

          // Verbal / phone approval: staff advanced stage without a client portal signature.
          if (!hasClientPortalSignature(existingSignature)) {
            await applyInternalClientDirectionSign(dealIdForSignedSync);
          }

          await syncSignedDealSideEffects(dealIdForSignedSync, user.id);
          await refreshProposalBuilder(dealIdForSignedSync);
        } catch (syncErr) {
          console.error('[ClientPortal] syncSignedDealSideEffects:', syncErr);
        }
      }
    } catch (err) {
      console.error('Error updating stage:', err);
      alert('Failed to update stage');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Move the stored portal workflow back to an earlier stage (e.g. undo advancing past 3D model).
   * Truncates stage history after the target so the client journey and stepper stay consistent.
   */
  const handleRevertStage = async (targetStage: PortalStage) => {
    if (!portalId || !portal) return;
    const stored = (activeProject?.currentStage || portal.currentStage) as PortalStage;
    const targetIdx = STAGE_ORDER.indexOf(targetStage);
    const storedIdx = STAGE_ORDER.indexOf(stored);
    if (targetIdx < 0 || storedIdx < 0 || storedIdx <= targetIdx) {
      alert('Workflow is not past that step.');
      return;
    }

    setSaving(true);
    try {
      const currentHistory = activeProject?.stageHistory || portal.stageHistory || [];
      const toDate = (d: unknown): Date => {
        if (d instanceof Date) return d;
        if (d && typeof (d as { toDate?: () => Date }).toDate === 'function') {
          return (d as { toDate: () => Date }).toDate();
        }
        return new Date();
      };

      const filtered = currentHistory.filter((h) => {
        const idx = STAGE_ORDER.indexOf(h.stage);
        if (idx < 0) return false;
        return idx <= targetIdx;
      });

      let newHistory = filtered.map((h) => ({
        ...h,
        enteredAt: toDate(h.enteredAt),
      }));

      const last = newHistory[newHistory.length - 1];
      if (!last || last.stage !== targetStage) {
        newHistory = [...newHistory, { stage: targetStage, enteredAt: new Date() }];
      }

      const historyForFirestore = newHistory.map((h) => ({
        ...h,
        enteredAt: h.enteredAt instanceof Date ? Timestamp.fromDate(h.enteredAt) : h.enteredAt,
      }));

      const firestoreUpdates: Record<string, any> = { updatedAt: Timestamp.now() };

      if (activeDealId) {
        firestoreUpdates[`projects.${activeDealId}.currentStage`] = targetStage;
        firestoreUpdates[`projects.${activeDealId}.stageHistory`] = historyForFirestore;
        // Pin the retreated stage: auto-bumps (released model, sent/approved
        // proposal, won deal, paid fee) must not silently re-advance the timeline.
        firestoreUpdates[`projects.${activeDealId}.stageManualOverride`] = true;
        firestoreUpdates[`projects.${activeDealId}.updatedAt`] = Timestamp.now();
      }

      const primaryDealId = portal.dealId;
      if (!activeDealId || activeDealId === primaryDealId) {
        firestoreUpdates.currentStage = targetStage;
        firestoreUpdates.stageHistory = historyForFirestore;
        firestoreUpdates.stageManualOverride = true;
      }

      await updateDoc(doc(db, 'clientPortals', portalId), firestoreUpdates);

      setPortal((prev) => {
        if (!prev) return null;
        const updated = { ...prev };
        if (!activeDealId || activeDealId === primaryDealId) {
          updated.currentStage = targetStage;
          updated.stageHistory = newHistory;
          updated.stageManualOverride = true;
        }
        if (activeDealId && updated.projects) {
          updated.projects = { ...updated.projects };
          updated.projects[activeDealId] = {
            ...(updated.projects[activeDealId] || {}),
            currentStage: targetStage,
            stageHistory: newHistory,
            stageManualOverride: true,
            updatedAt: new Date(),
          } as PortalProject;
        }
        return updated;
      });

      setSelectedStep(targetStage);
    } catch (err) {
      console.error('Error reverting stage:', err);
      alert('Failed to revert workflow step');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStepDetails = async (updates: Record<string, any>) => {
    if (!portalId) return;
    setSaving(true);
    try {
      const firestoreUpdates: Record<string, any> = { updatedAt: Timestamp.now() };

      if (activeDealId) {
        for (const [key, value] of Object.entries(updates)) {
          firestoreUpdates[`projects.${activeDealId}.${key}`] = value;
        }
        firestoreUpdates[`projects.${activeDealId}.updatedAt`] = Timestamp.now();
      }

      // Dual-write flat fields for primary deal (backward compat)
      const primaryDealId = portal?.dealId;
      if (!activeDealId || activeDealId === primaryDealId) {
        Object.assign(firestoreUpdates, updates);
      }

      await updateDoc(doc(db, 'clientPortals', portalId), firestoreUpdates);

      setPortal(prev => {
        if (!prev) return null;
        const updatedPortal = { ...prev, ...(!activeDealId || activeDealId === primaryDealId ? updates : {}) };
        if (activeDealId && updatedPortal.projects) {
          updatedPortal.projects = { ...updatedPortal.projects };
          updatedPortal.projects[activeDealId] = {
            ...(updatedPortal.projects[activeDealId] || {}),
            ...updates,
            updatedAt: new Date(),
          } as PortalProject;
        }
        return updatedPortal;
      });
    } catch (err) {
      console.error('Error saving:', err);
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveWalkthroughProjectSetting = async (mode: 'inherit' | 'on' | 'off') => {
    if (!portalId || !activeDealId) return;
    setSavingWalkthroughToggle(true);
    try {
      const projectOverride = mode === 'inherit' ? undefined : mode === 'on';
      const effective = computeModelWalkthroughEffective({
        globalDefault: globalWalkthroughDefault,
        projectOverride,
      });
      const firestoreUpdates: Record<string, any> = {
        updatedAt: Timestamp.now(),
        [`projects.${activeDealId}.modelWalkthroughEffective`]: effective,
        [`projects.${activeDealId}.updatedAt`]: Timestamp.now(),
      };
      if (mode === 'inherit') {
        firestoreUpdates[`projects.${activeDealId}.modelWalkthroughEnabled`] = deleteField();
      } else {
        firestoreUpdates[`projects.${activeDealId}.modelWalkthroughEnabled`] = mode === 'on';
      }
      await updateDoc(doc(db, 'clientPortals', portalId), firestoreUpdates);
      setWalkthroughOverride(mode);
      setPortal(prev => {
        if (!prev?.projects?.[activeDealId]) return prev;
        const next = { ...prev, projects: { ...prev.projects } };
        const proj = { ...next.projects![activeDealId] } as PortalProject & {
          modelWalkthroughEnabled?: boolean;
          modelWalkthroughEffective?: boolean;
        };
        if (mode === 'inherit') delete proj.modelWalkthroughEnabled;
        else proj.modelWalkthroughEnabled = mode === 'on';
        proj.modelWalkthroughEffective = effective;
        next.projects![activeDealId] = proj;
        return next;
      });
    } catch (err) {
      console.error('Failed to save walkthrough setting', err);
      alert('Failed to save walkthrough setting');
    } finally {
      setSavingWalkthroughToggle(false);
    }
  };

  const walkthroughEffectiveForProject = useMemo(() => {
    const override =
      walkthroughOverride === 'inherit'
        ? undefined
        : walkthroughOverride === 'on';
    return computeModelWalkthroughEffective({
      globalDefault: globalWalkthroughDefault,
      projectOverride: override,
    });
  }, [globalWalkthroughDefault, walkthroughOverride]);

  const handleResendApprovalNotifications = async (
    deliverableType: 'concept' | 'model' | 'plans',
  ) => {
    if (!portalId || !activeDealId) return;
    setResendingApprovalNotify(deliverableType);
    try {
      await resendPortalDeliverableApprovalNotifications({
        portalId,
        dealId: activeDealId,
        deliverableType,
      });
      alert('Team notifications sent (Slack, CRM timeline, and in-app alerts).');
    } catch (err) {
      console.error('[ClientPortalManagement] resend approval notifications failed', err);
      alert('Could not re-send approval notifications. Check the console for details.');
    } finally {
      setResendingApprovalNotify(null);
    }
  };

  const handleFileUpload = async (file: File, type: 'model' | 'plans') => {
    if (!portalId || !portal) return;
    const setUploading = type === 'model' ? setUploadingModel : setUploadingPlans;
    setUploading(true);
    try {
      const fileName = `portal-files/${portalId}/${type}-${Date.now()}-${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      const updateField = type === 'model' ? 'modelUrl' : 'plansUrl';

      const firestoreUpdates: Record<string, any> = {
        [updateField]: downloadUrl,
        updatedAt: Timestamp.now(),
      };
      if (activeDealId) {
        firestoreUpdates[`projects.${activeDealId}.${updateField}`] = downloadUrl;
        firestoreUpdates[`projects.${activeDealId}.updatedAt`] = Timestamp.now();
        // Manual one-off upload from this management page wipes the "from Project Hub"
        // provenance — the badge should disappear immediately because the file the
        // client now sees is no longer a release.
        const sourcePrefix = type === 'model' ? 'modelSource' : 'plansSource';
        firestoreUpdates[`projects.${activeDealId}.${sourcePrefix}ReleaseId`] = deleteField();
        firestoreUpdates[`projects.${activeDealId}.${sourcePrefix}ProjectId`] = deleteField();
        firestoreUpdates[`projects.${activeDealId}.${sourcePrefix}VersionLabel`] = deleteField();
      }

      await updateDoc(doc(db, 'clientPortals', portalId), firestoreUpdates);

      if (type === 'model') {
        setModelUrl(downloadUrl);
      } else {
        setPlansUrl(downloadUrl);
      }
      setPortal(prev => {
        if (!prev) return null;
        const updated = { ...prev, [updateField]: downloadUrl };
        if (activeDealId && updated.projects?.[activeDealId]) {
          updated.projects = { ...updated.projects };
          const proj = { ...updated.projects[activeDealId], [updateField]: downloadUrl } as any;
          if (type === 'model') {
            delete proj.modelSourceReleaseId;
            delete proj.modelSourceProjectId;
            delete proj.modelSourceVersionLabel;
          } else {
            delete proj.plansSourceReleaseId;
            delete proj.plansSourceProjectId;
            delete proj.plansSourceVersionLabel;
          }
          updated.projects[activeDealId] = proj;
        }
        return updated;
      });
    } catch (err) {
      console.error(`Error uploading ${type} file:`, err);
      alert(`Failed to upload ${type} file`);
    } finally {
      setUploading(false);
    }
  };

  const handleModelImageUpload = async (file: File) => {
    if (!portalId || !portal) return;
    setUploadingModelImage(true);
    try {
      const fileName = `portal-files/${portalId}/model-image-${Date.now()}-${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      const firestoreUpdates: Record<string, any> = {
        modelImageUrl: downloadUrl,
        updatedAt: Timestamp.now(),
      };
      if (activeDealId) {
        firestoreUpdates[`projects.${activeDealId}.modelImageUrl`] = downloadUrl;
        firestoreUpdates[`projects.${activeDealId}.updatedAt`] = Timestamp.now();
      }

      await updateDoc(doc(db, 'clientPortals', portalId), firestoreUpdates);
      setModelImageUrl(downloadUrl);
      setPortal(prev => {
        if (!prev) return null;
        const updated = { ...prev, modelImageUrl: downloadUrl };
        if (activeDealId && updated.projects?.[activeDealId]) {
          updated.projects = { ...updated.projects };
          updated.projects[activeDealId] = { ...updated.projects[activeDealId], modelImageUrl: downloadUrl };
        }
        return updated;
      });
    } catch (err) {
      console.error('Error uploading model image:', err);
      alert('Failed to upload image');
    } finally {
      setUploadingModelImage(false);
    }
  };

  const handleCopyLink = () => {
    if (!portal) return;
    const url = `${window.location.origin}/portal/${portal.slug}`;
    navigator.clipboard.writeText(`${url}\nPIN: ${portal.accessPin}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCreateStakeholderLink = async () => {
    if (!portalId || !portal?.slug || !newStakeholderName.trim() || !user) return;
    setStakeholderBusy(true);
    try {
      const linkRef = doc(collection(db, 'portalStakeholderLinks'));
      await setDoc(linkRef, {
        portalId,
        portalSlug: portal.slug,
        displayName: newStakeholderName.trim(),
        createdAt: Timestamp.now(),
        createdBy: user.id,
        revokedAt: null,
      });
      setNewStakeholderName('');
    } catch (e) {
      console.error('Create stakeholder link:', e);
      alert('Could not create stakeholder link');
    } finally {
      setStakeholderBusy(false);
    }
  };

  const handleRevokeStakeholderLink = async (linkDocId: string) => {
    if (!window.confirm('Revoke this stakeholder link? It will stop working immediately.')) return;
    try {
      await updateDoc(doc(db, 'portalStakeholderLinks', linkDocId), {
        revokedAt: Timestamp.now(),
      });
    } catch (e) {
      console.error('Revoke stakeholder link:', e);
      alert('Could not revoke link');
    }
  };

  const handleCopyStakeholderUrl = (token: string) => {
    if (!portal) return;
    const url = `${window.location.origin}/portal/${portal.slug}?stakeholderToken=${encodeURIComponent(token)}`;
    navigator.clipboard.writeText(url);
    setCopiedStakeholderId(token);
    setTimeout(() => setCopiedStakeholderId(null), 2000);
  };

  const handleEditClientInfo = () => {
    if (!lead) return;
    setEditEmail(lead.email || '');
    setEditPhone(lead.phone || '');
    setEditCompany(lead.company || '');
    setEditLocation(lead.location || '');
    setEditProjectType(lead.projectType || '');
    setEditBudget(lead.budget ? lead.budget.toString() : '');
    setEditingClientInfo(true);
  };

  const handleSaveClientInfo = async () => {
    if (!lead?.id) return;
    setSavingClientInfo(true);
    try {
      await updateLead(lead.id, {
        email: editEmail,
        phone: editPhone,
        company: editCompany,
        location: editLocation,
        projectType: editProjectType,
        budget: parseFloat(editBudget) || 0,
      });
      // Sync company to portal document
      if (portalId) {
        try {
          await updateDoc(doc(db, 'clientPortals', portalId), {
            clientCompany: editCompany,
            updatedAt: Timestamp.now(),
          });
          setPortal(prev => prev ? { ...prev, clientCompany: editCompany } : null);
        } catch { /* non-blocking */ }
      }
      const clientDisplayName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim();
      for (const deal of relatedDeals) {
        if (!deal.id) continue;
        try {
          await updateEnhancedDeal(deal.id, { company: editCompany || undefined });
        } catch { /* non-blocking */ }
      }
      try {
        await syncProposalClientIdentityForDeals(
          relatedDeals.map(d => d.id).filter(Boolean) as string[],
          { clientName: clientDisplayName || undefined, leadCompany: editCompany },
        );
      } catch (syncErr) {
        console.warn('[ClientPortal] proposal client identity sync failed:', syncErr);
      }
      setEditingClientInfo(false);
    } catch (err) {
      console.error('Error updating client info:', err);
      alert('Failed to save client info');
    } finally {
      setSavingClientInfo(false);
    }
  };

  // ═══════════════════════════════════════════
  // STEP DETAIL CARD RENDERERS
  // ═══════════════════════════════════════════

  // Resolve agreement data strictly from active project — never fall back to portal-level
  const agreementData = useMemo(() => {
    if (activeProject?.agreementSigned) return activeProject;
    // Only use portal-level for the primary deal (backward compat for portals before multi-project)
    if (!activeDealId || activeDealId === portal?.dealId) {
      if (portal?.agreementSigned) return portal;
    }
    return null;
  }, [activeProject, portal, activeDealId]);

  useEffect(() => {
    if (agreementData?.agreementSigned && agreementData.agreementSignedAt) {
      setAdminContractSignedDate(toDateInputValue(agreementData.agreementSignedAt));
    } else {
      setAdminContractSignedDate('');
    }
  }, [agreementData?.agreementSigned, agreementData?.agreementSignedAt]);

  /**
   * Per-stage gate for the unified Advance button — preserves the readiness
   * requirements the old per-step advance buttons enforced. Returns a
   * human-readable reason when advancing is blocked, or null when clear.
   */
  const getAdvanceBlockedReason = (): string | null => {
    switch (storedPortalStage) {
      case 'design-fee': {
        const ready = !!agreementData?.agreementSigned || designFeePaid || designFeeSkipped;
        return ready ? null : 'Agreement must be signed or fee paid (or step skipped) before advancing.';
      }
      case '3d-model': {
        const ready = !!effectiveModelUrl || !!modelImageUrl || modelStepSkipped;
        return ready ? null : 'Add a 3D model link or image (or skip the step) before advancing.';
      }
      case 'quote': {
        const status = proposalBuilderData?.status;
        const ready = status === 'sent' || status === 'opened' || status === 'approved';
        return ready ? null : 'Send the proposal to the client before advancing to Signed.';
      }
      case '2d-plans':
        return effectivePlansUrl ? null : 'Upload plans before advancing to Production.';
      case 'in-production':
        return productionStatus === 'complete' ? null : 'Set production status to Complete before advancing to Delivery.';
      default:
        return null;
    }
  };

  useEffect(() => {
    const payAt = activeProject?.designFeePaidAt ?? portal?.designFeePaidAt;
    setAdminDesignFeePaidDate(toDateInputValue(payAt));
  }, [activeProject?.designFeePaidAt, portal?.designFeePaidAt]);

  const saveAdminDesignFeePaidDate = useCallback(async () => {
    if (!canEditPortalMilestoneDates || !portalId) return;
    if (!designFeePaid || !adminDesignFeePaidDate.trim()) {
      alert('Set the design fee paid date to save.');
      return;
    }
    const d = parseLocalDateInput(adminDesignFeePaidDate.trim());
    if (Number.isNaN(d.getTime())) {
      alert('Invalid design fee paid date.');
      return;
    }
    const updates: Record<string, unknown> = { designFeePaidAt: Timestamp.fromDate(d) };
    setSavingAdminDesignFeeDate(true);
    try {
      await handleSaveStepDetails(updates as Record<string, any>);
      const letterId = activeProject?.engagementLetterId || portal?.engagementLetterId;
      if (letterId) {
        try {
          await updateDoc(doc(db, 'engagementLetters', letterId), {
            paidAt: updates.designFeePaidAt,
            updatedAt: Timestamp.now(),
          } as Record<string, any>);
        } catch (letterErr) {
          console.warn('Engagement letter paid date sync skipped:', letterErr);
        }
      }
      if (relatedDeal?.id) {
        await updateEnhancedDeal(relatedDeal.id, {
          designPaymentDate: (updates.designFeePaidAt as Timestamp).toDate(),
        } as any);
      }
    } catch (err) {
      console.error('saveAdminDesignFeePaidDate:', err);
      alert('Failed to save design fee date. Check permissions and try again.');
    } finally {
      setSavingAdminDesignFeeDate(false);
    }
  }, [
    canEditPortalMilestoneDates,
    portalId,
    adminDesignFeePaidDate,
    designFeePaid,
    activeProject?.engagementLetterId,
    portal?.engagementLetterId,
    relatedDeal?.id,
    updateEnhancedDeal,
    handleSaveStepDetails,
  ]);

  const saveAdminAgreementSignedDate = useCallback(async () => {
    if (!canEditPortalMilestoneDates || !portalId) return;
    if (!agreementData?.agreementSigned || !adminContractSignedDate.trim()) {
      alert('Set the design agreement signed date to save.');
      return;
    }
    const d = parseLocalDateInput(adminContractSignedDate.trim());
    if (Number.isNaN(d.getTime())) {
      alert('Invalid design agreement signed date.');
      return;
    }
    const updates: Record<string, unknown> = { agreementSignedAt: Timestamp.fromDate(d) };
    setSavingAdminAgreementSignedDate(true);
    try {
      await handleSaveStepDetails(updates as Record<string, any>);
      const letterId = activeProject?.engagementLetterId || portal?.engagementLetterId;
      if (letterId) {
        try {
          await updateDoc(doc(db, 'engagementLetters', letterId), {
            signedAt: updates.agreementSignedAt,
            'signature.signedAt': updates.agreementSignedAt,
            updatedAt: Timestamp.now(),
          } as Record<string, any>);
        } catch (letterErr) {
          console.warn('Engagement letter signed date sync skipped:', letterErr);
        }
      }
      const signedD = (updates.agreementSignedAt as Timestamp).toDate();
      try {
        await correctLatestAgreementPortalActivityTimestamp(
          portalId,
          activeDealId || portal?.dealId || null,
          signedD,
        );
      } catch (actErr) {
        console.warn('Portal activity timestamp sync skipped:', actErr);
      }
    } catch (err) {
      console.error('saveAdminAgreementSignedDate:', err);
      alert('Failed to save agreement signed date. Check permissions and try again.');
    } finally {
      setSavingAdminAgreementSignedDate(false);
    }
  }, [
    canEditPortalMilestoneDates,
    portalId,
    agreementData?.agreementSigned,
    adminContractSignedDate,
    activeProject?.engagementLetterId,
    portal?.engagementLetterId,
    portal?.dealId,
    activeDealId,
    handleSaveStepDetails,
  ]);

  const handleViewAgreementPdf = async () => {
    if (!portal || !agreementData || generatingAgreementPdf) return;
    const pdfWindow = window.open('', '_blank');
    setGeneratingAgreementPdf(true);
    try {
      const signedAt = agreementData.agreementSignedAt || new Date();
      const services: { name: string; price: number }[] = agreementData.agreementServices?.length
        ? agreementData.agreementServices.map(s => ({ name: s.name, price: s.price }))
        : [{ name: 'Design Services', price: agreementData.agreementTotal || 0 }];

      const blob = await generateSignedAgreementPdf({
        clientName: portal.clientName,
        projectName: activeProject?.projectName || undefined,
        signedBy: agreementData.agreementSignedBy || portal.clientName,
        signedAt,
        signatureDataUrl: agreementData.agreementSignature || '',
        services,
        totalAmount: agreementData.agreementTotal || services.reduce((sum, s) => sum + s.price, 0),
      });

      const url = URL.createObjectURL(blob);
      if (pdfWindow) {
        pdfWindow.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('Error generating agreement PDF:', err);
      pdfWindow?.close();
    } finally {
      setGeneratingAgreementPdf(false);
    }
  };

  const renderStepDesignFee = () => {
    const isSigned = !!agreementData?.agreementSigned;
    const isPaid = designFeePaid;
    const agr = agreementData;

    const badgeLabel = isPaid ? 'Paid' : isSigned ? 'Signed' : null;
    const badgeClass = isPaid ? 'pm-badge-paid' : isSigned ? 'pm-badge-signed' : '';

    return (
      <div className="pm-detail-card">
        <div className="pm-detail-header">
          <FileSignature size={20} />
          <div>
            <h3>Concept Design Fee</h3>
            <p>Design services agreement and payment tracking.</p>
          </div>
          <div className="pm-header-right">
            {leadId && (activeDealId || portal?.dealId) && (
              <button
                type="button"
                className="pm-quote-action-sm"
                onClick={() =>
                  navigate(
                    `/client/${leadId}/project/${activeDealId || portal?.dealId}/board`,
                  )
                }
                title="Open full-screen inspiration board for this project"
              >
                <LayoutGrid size={12} />
                Inspiration Board
              </button>
            )}
            {badgeLabel && (
              <span className={`pm-detail-badge ${badgeClass}`}>
                {badgeLabel}
              </span>
            )}
          </div>
        </div>

        <div className="pm-detail-body">
          <div className="cw-stack">
            {/* Key facts */}
            <div className="cw-facts">
              <div className="cw-fact">
                <span className="cw-fact-label">Agreement</span>
                <span className="cw-fact-value">
                  {isSigned
                    ? `Signed${agr?.agreementSignedBy ? ` by ${agr.agreementSignedBy}` : ''}${agr?.agreementSignedAt ? ` · ${agr.agreementSignedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}`
                    : 'Awaiting signature'}
                </span>
              </div>
              <div className="cw-fact">
                <span className="cw-fact-label">Payment</span>
                <span className="cw-fact-value">
                  {isPaid ? (() => {
                    const payData = activeProject || portal;
                    const paidAtRaw = payData?.designFeePaidAt as any;
                    const paidAt = paidAtRaw instanceof Date ? paidAtRaw : paidAtRaw?.toDate?.() || (paidAtRaw ? new Date(paidAtRaw) : null);
                    return (
                      <>
                        <strong>{`Paid${payData?.amountPaid ? ` $${payData.amountPaid.toLocaleString()}` : ''}`}</strong>
                        {paidAt ? ` · ${paidAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                        {payData?.stripeSessionId && (
                          <a
                            href={`https://dashboard.stripe.com/payments/${payData.stripeSessionId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View in Stripe"
                            style={{ display: 'inline-flex', color: 'inherit' }}
                          >
                            <ExternalLink size={10} />
                          </a>
                        )}
                      </>
                    );
                  })() : 'Unpaid'}
                </span>
              </div>
              <div className="cw-fact">
                <span className="cw-fact-label">Fee total</span>
                <span className="cw-fact-value cw-fact-strong">
                  ${(isSigned && agr?.agreementTotal != null
                    ? agr.agreementTotal
                    : (activeProject?.customDesignFeeServices || globalFeeServices).reduce((s, v) => s + v.price, 0)
                  ).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Services (editable before signing, read-only after) */}
            {!isSigned ? (() => {
                const projectServices = activeProject?.customDesignFeeServices;
                const effectiveServices = projectServices || globalFeeServices;
                const hasCustom = !!projectServices?.length;

                const handleStartEdit = () => {
                  setFeeServicesLocal(JSON.parse(JSON.stringify(effectiveServices)));
                  setEditingFeeServices(true);
                };
                const handleCancelEdit = () => { setEditingFeeServices(false); setFeeServicesLocal([]); };
                const handleSaveFeeServices = async () => {
                  if (!portalId || !activeDealId) return;
                  setSavingFeeServices(true);
                  try {
                    const cleaned = feeServicesLocal.filter(s => s.name.trim());
                    await handleSaveStepDetails({ customDesignFeeServices: cleaned });
                    setPortal(prev => {
                      if (!prev || !activeDealId) return prev;
                      const next = { ...prev, projects: { ...(prev.projects || {}) } };
                      if (next.projects![activeDealId]) {
                        next.projects![activeDealId] = { ...next.projects![activeDealId], customDesignFeeServices: cleaned };
                      }
                      return next;
                    });
                    setEditingFeeServices(false);
                  } catch (err) { console.error('Failed to save fee services:', err); }
                  finally { setSavingFeeServices(false); }
                };
                const handleResetToGlobal = async () => {
                  if (!portalId || !activeDealId) return;
                  setSavingFeeServices(true);
                  try {
                    const updates: Record<string, any> = { updatedAt: Timestamp.now() };
                    updates[`projects.${activeDealId}.customDesignFeeServices`] = deleteField();
                    updates[`projects.${activeDealId}.updatedAt`] = Timestamp.now();
                    await updateDoc(doc(db, 'clientPortals', portalId), updates);
                    setPortal(prev => {
                      if (!prev || !activeDealId) return prev;
                      const next = { ...prev, projects: { ...(prev.projects || {}) } };
                      if (next.projects![activeDealId]) { const p = { ...next.projects![activeDealId] }; delete p.customDesignFeeServices; next.projects![activeDealId] = p; }
                      return next;
                    });
                    setEditingFeeServices(false);
                  } catch (err) { console.error('Failed to reset fee services:', err); }
                  finally { setSavingFeeServices(false); }
                };

                return (
                  <>
                    <div className="cw-section-label">
                      <span>
                        <DollarSign size={12} /> Services
                        {hasCustom && <span className="cw-custom-chip">Custom</span>}
                      </span>
                      {!editingFeeServices && (
                        <button type="button" className="cw-mini-btn" onClick={handleStartEdit}>
                          <Pencil size={9} /> Customize
                        </button>
                      )}
                    </div>

                    {!editingFeeServices ? (
                      <div className="cw-svc-table">
                        {effectiveServices.map(svc => (
                          <div key={svc.id} className="cw-svc-row">
                            <span className="cw-svc-name">
                              {svc.name}
                              {svc.required && <span className="cw-svc-req">required</span>}
                            </span>
                            <span className="cw-svc-price">${svc.price.toLocaleString()}</span>
                          </div>
                        ))}
                        <div className="cw-svc-total">
                          <span>Max Total</span>
                          <span>${effectiveServices.reduce((s, v) => s + v.price, 0).toLocaleString()}</span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 10, background: 'var(--color-surface)' }}>
                        {feeServicesLocal.map((svc, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                            <input value={svc.name} onChange={e => { const next = [...feeServicesLocal]; next[idx] = { ...svc, name: e.target.value }; setFeeServicesLocal(next); }} placeholder="Service name" style={{ flex: 1, fontSize: 12, padding: '5px 7px', border: '1px solid var(--color-border)', borderRadius: 5, fontFamily: 'inherit', minWidth: 0 }} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>$</span>
                              <input type="number" value={svc.price} onChange={e => { const next = [...feeServicesLocal]; next[idx] = { ...svc, price: Number(e.target.value) || 0 }; setFeeServicesLocal(next); }} style={{ width: 70, fontSize: 12, padding: '5px 7px', border: '1px solid var(--color-border)', borderRadius: 5, fontFamily: 'inherit' }} />
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 10, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                              <input type="checkbox" checked={!!svc.required} onChange={e => { const next = [...feeServicesLocal]; next[idx] = { ...svc, required: e.target.checked }; setFeeServicesLocal(next); }} style={{ width: 12, height: 12 }} /> Req
                            </label>
                            <button onClick={() => setFeeServicesLocal(prev => prev.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-text-muted)' }} title="Remove"><X size={12} /></button>
                          </div>
                        ))}
                        <button onClick={() => setFeeServicesLocal(prev => [...prev, { id: `custom-${Date.now()}`, name: '', price: 0, description: '' }])} style={{ fontSize: 11, color: 'var(--pm-accent)', background: 'none', border: '1px dashed var(--color-border)', borderRadius: 5, padding: '5px 8px', cursor: 'pointer', fontFamily: 'inherit', width: '100%', marginBottom: 8 }}>
                          <Plus size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Add Service
                        </button>
                        <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', alignItems: 'center' }}>
                          {hasCustom && <button onClick={handleResetToGlobal} disabled={savingFeeServices} style={{ fontSize: 10, color: 'var(--color-text-secondary)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>Reset to defaults</button>}
                          <button onClick={handleCancelEdit} style={{ fontSize: 11, color: 'var(--color-text-secondary)', background: 'white', border: '1px solid var(--color-border)', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                          <button onClick={handleSaveFeeServices} disabled={savingFeeServices} style={{ fontSize: 11, color: 'white', background: '#2C3E2F', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 3 }}>
                            {savingFeeServices ? <Loader2 size={11} className="pm-spin" /> : <Save size={11} />} Save
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })() : agr?.agreementServices && agr.agreementServices.length > 0 ? (
                <>
                  <div className="cw-section-label">
                    <span>
                      <ShieldCheck size={12} /> Signed services
                    </span>
                    <button type="button" className="cw-mini-btn" onClick={handleViewAgreementPdf} disabled={generatingAgreementPdf}>
                      {generatingAgreementPdf ? <Loader2 size={9} className="pm-spin" /> : <FileSignature size={9} />}
                      {generatingAgreementPdf ? 'Generating…' : 'View Signed Agreement'}
                    </button>
                  </div>
                  <div className="cw-svc-table">
                    {agr.agreementServices.map((svc, i) => (
                      <div key={i} className="cw-svc-row">
                        <span className="cw-svc-name">{svc.name}</span>
                        <span className="cw-svc-price">${svc.price.toLocaleString()}</span>
                      </div>
                    ))}
                    {agr.agreementTotal != null && (
                      <div className="cw-svc-total">
                        <span>Total</span><span>${agr.agreementTotal.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="cw-empty-line">
                  <Clock size={14} />
                  Awaiting client signature on their portal.
                </div>
              )}

            {/* Payment received toggle */}
            <label className="cw-toggle-row" data-tone="good" data-on={designFeePaid}>
              <input
                type="checkbox"
                checked={designFeePaid}
                onChange={async (e) => {
                  const paid = e.target.checked;
                  setDesignFeePaid(paid);
                  const updates: Record<string, any> = { designFeePaid: paid };
                  const existingPaidAt = activeProject?.designFeePaidAt || portal?.designFeePaidAt;
                  if (paid) {
                    if (!existingPaidAt) updates.designFeePaidAt = Timestamp.now();
                  } else {
                    updates.designFeePaidAt = deleteField();
                  }
                  handleSaveStepDetails(updates);
                  if (paid && relatedDeal) {
                    try { await updateEnhancedDeal(relatedDeal.id!, { stageId: 'design', designSubStage: 'in-design' } as any); }
                    catch (err) { console.warn('Failed to advance deal to in-design:', err); }
                  }
                }}
              />
              <span>Payment Received</span>
              {designFeePaid && <span className="cw-toggle-row-hint">Visible to client</span>}
            </label>

            {/* Skip Design Fee */}
            <label className="cw-toggle-row" data-tone="warn" data-on={designFeeSkipped}>
              <input
                type="checkbox"
                checked={designFeeSkipped}
                onChange={(e) => { setDesignFeeSkipped(e.target.checked); handleSaveStepDetails({ designFeeSkipped: e.target.checked }); }}
              />
              <span>Skip — pre-existing designs</span>
            </label>

            <label className="cw-toggle-row" data-on={inspirationBoardPortalEnabled}>
              <input
                type="checkbox"
                checked={inspirationBoardPortalEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setInspirationBoardPortalEnabled(enabled);
                  handleSaveStepDetails({ inspirationBoardPortalEnabled: enabled });
                }}
              />
              <span>Show inspiration board on client portal</span>
              {inspirationBoardPortalEnabled && (
                <span className="cw-toggle-row-hint">Visible on the design fee step even when the fee is skipped</span>
              )}
            </label>

            {/* Admin milestone dates (permission-gated, collapsed by default) */}
            {canEditPortalMilestoneDates && (designFeePaid || agreementData?.agreementSigned) && (
              <details className="cw-disclosure">
                <summary>
                  <ChevronDown size={12} />
                  <Calendar size={12} />
                  Admin: milestone dates
                </summary>
                <div className="cw-disclosure-body">
                  {designFeePaid && (
                    <>
                      <div className="cw-form-row">
                        <span className="cw-form-label">Fee paid date</span>
                        <input
                          type="date"
                          className="pm-input"
                          value={adminDesignFeePaidDate}
                          onChange={(e) => setAdminDesignFeePaidDate(e.target.value)}
                        />
                        <button
                          type="button"
                          className="cw-mini-btn"
                          onClick={() => { void saveAdminDesignFeePaidDate(); }}
                          disabled={savingAdminDesignFeeDate || saving}
                        >
                          {savingAdminDesignFeeDate ? <Loader2 size={9} className="pm-spin" /> : <Save size={9} />}
                          {savingAdminDesignFeeDate ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                      <p className="cw-disclosure-hint">
                        Updates this portal (and project), the linked engagement letter paid date, the deal design
                        payment date, and design-fee revenue timing in Sales Activity.
                      </p>
                    </>
                  )}
                  {agreementData?.agreementSigned && (
                    <>
                      <div className="cw-form-row">
                        <span className="cw-form-label">Signed date</span>
                        <input
                          type="date"
                          className="pm-input"
                          value={adminContractSignedDate}
                          onChange={(e) => setAdminContractSignedDate(e.target.value)}
                        />
                        <button
                          type="button"
                          className="cw-mini-btn"
                          onClick={() => { void saveAdminAgreementSignedDate(); }}
                          disabled={savingAdminAgreementSignedDate || saving}
                        >
                          {savingAdminAgreementSignedDate ? <Loader2 size={9} className="pm-spin" /> : <Save size={9} />}
                          {savingAdminAgreementSignedDate ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                      <p className="cw-disclosure-hint">
                        For the design-services agreement from this step (not the construction quote). Updates the
                        portal, linked engagement letter signature dates, and the latest design-agreement sign row on
                        the activity timeline.
                      </p>
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStep3DModel = () => {
    const designSubStage = (relatedDeal as any)?.designSubStage as DesignSubStage | undefined;
    // Fall back to stage history entry for '3d-model' or today if designFeePaidAt isn't set
    const paidAt = activeProject?.designFeePaidAt || portal?.designFeePaidAt
      || portal?.stageHistory?.find(h => h.stage === '3d-model')?.enteredAt
      || (designFeePaid ? new Date() : null);
    const countdown = paidAt && designSubStage && designSubStage !== 'design-complete'
      ? getBusinessDaysRemaining(paidAt, 10) : null;

    return (
      <div className="pm-detail-card">
        <div className="pm-detail-header">
          <Box size={20} />
          <div>
            <h3>3D Model</h3>
            <p>Upload the interactive HTML model for client viewing.</p>
          </div>
          <div className="pm-header-badges">
            {designSubStage && (
              <span className="pm-detail-badge" style={{ background: SUB_STAGE_COLORS[designSubStage], color: 'white' }}>
                {SUB_STAGE_LABELS[designSubStage]}
              </span>
            )}
            {countdown && (
              <span className={`pm-countdown-badge ${countdown.remaining <= 0 ? 'overdue' : countdown.remaining <= 2 ? 'urgent' : ''}`}>
                <Clock size={11} />
                {countdown.remaining > 0
                  ? `${countdown.remaining}d`
                  : countdown.remaining === 0
                    ? 'Due today'
                    : `${Math.abs(countdown.remaining)}d over`}
              </span>
            )}
            {activeProject?.modelApproval?.status === 'approved' && (
              <>
                <span className="pm-detail-badge pm-badge-paid" title={formatApprovalBadge(activeProject.modelApproval) || undefined}>
                  3D approved
                </span>
                <button
                  type="button"
                  className="cw-icon-btn"
                  title="Re-send Slack & CRM notifications for 3D model approval"
                  disabled={resendingApprovalNotify === 'model'}
                  onClick={() => void handleResendApprovalNotifications('model')}
                >
                  {resendingApprovalNotify === 'model' ? (
                    <Loader2 size={11} className="spin" />
                  ) : (
                    <RefreshCw size={11} />
                  )}
                </button>
              </>
            )}
            {activeProject?.conceptApproval?.status === 'approved' && (
              <>
                <span className="pm-detail-badge pm-badge-paid" title={formatApprovalBadge(activeProject.conceptApproval) || undefined}>
                  {typeof activeProject.conceptApproval.releaseVersionIndex === 'number'
                    ? `2D concept v${activeProject.conceptApproval.releaseVersionIndex} approved`
                    : '2D concept approved'}
                </span>
                <button
                  type="button"
                  className="cw-icon-btn"
                  title="Re-send Slack & CRM notifications for 2D concept approval"
                  disabled={resendingApprovalNotify === 'concept'}
                  onClick={() => void handleResendApprovalNotifications('concept')}
                >
                  {resendingApprovalNotify === 'concept' ? (
                    <Loader2 size={11} className="spin" />
                  ) : (
                    <RefreshCw size={11} />
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="pm-detail-body">
          <div className="cw-stack">
            {/* HTML model slot */}
            <input ref={modelFileRef} type="file" accept=".html" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileUpload(file, 'model'); }} style={{ display: 'none' }} />
            <div className="cw-file-row" data-state={effectiveModelUrl ? 'filled' : 'empty'}>
              <span className="cw-file-row-icon"><Box size={13} /></span>
              <span className="cw-file-row-text">
                <span className="cw-file-row-title">
                  {effectiveModelUrl
                    ? (modelFromHubOnly ? '3D model — released from Project Hub' : '3D model uploaded')
                    : 'Interactive 3D model (.html)'}
                </span>
                {displayModelRelease && (
                  <span className="cw-file-row-sub" title={displayModelRelease.name}>
                    {displayModelRelease.sourceStreamTitle || '3D Model'}
                    {displayModelRelease.versionLabel ? ` · ${displayModelRelease.versionLabel}` : ''}
                    {displayModelRelease.versionIndex != null ? ` · #${displayModelRelease.versionIndex}` : ''}
                    {displayModelRelease.releaseVersionIndex != null ? ` · v${displayModelRelease.releaseVersionIndex}` : ''}
                    {displayModelRelease.sourceProjectId && displayModelRelease.sourceStreamId && (
                      <Link to={`/projects/${displayModelRelease.sourceProjectId}?tab=files&track=${displayModelRelease.sourceStreamId}`}>
                        Open in Hub <ExternalLink size={9} aria-hidden />
                      </Link>
                    )}
                  </span>
                )}
              </span>
              {effectiveModelUrl ? (
                <span className="cw-file-row-actions">
                  <a href={portalHostedAssetHref(effectiveModelUrl)} target="_blank" rel="noopener noreferrer" className="cw-icon-btn" title="Preview in new tab"><Eye size={13} /></a>
                  {!modelFromHubOnly && (
                    <>
                      <button className="cw-icon-btn" onClick={() => modelFileRef.current?.click()} disabled={uploadingModel} title="Replace">
                        {uploadingModel ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
                      </button>
                      <button className="cw-icon-btn danger" onClick={async () => {
                        if (!portalId || !window.confirm('Remove the 3D model?')) return;
                        const updates: Record<string, any> = { modelUrl: '', updatedAt: Timestamp.now() };
                        if (activeDealId) { updates[`projects.${activeDealId}.modelUrl`] = ''; updates[`projects.${activeDealId}.updatedAt`] = Timestamp.now(); }
                        await updateDoc(doc(db, 'clientPortals', portalId), updates);
                        setModelUrl('');
                        setPortal(prev => { if (!prev) return null; const updated = { ...prev, modelUrl: '' }; if (activeDealId && updated.projects?.[activeDealId]) { updated.projects = { ...updated.projects }; updated.projects[activeDealId] = { ...updated.projects[activeDealId], modelUrl: '' } as any; } return updated; });
                      }} title="Remove model"><Trash2 size={13} /></button>
                    </>
                  )}
                </span>
              ) : (
                <button className="cw-file-upload-btn" onClick={() => modelFileRef.current?.click()} disabled={uploadingModel}>
                  {uploadingModel ? <><Loader2 size={12} className="spin" /> Uploading…</> : <><Upload size={12} /> Upload</>}
                </button>
              )}
            </div>

            {/* Image (alternative) slot */}
            <input ref={modelImageFileRef} type="file" accept="image/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleModelImageUpload(file); }} style={{ display: 'none' }} />
            <div className="cw-file-row" data-state={modelImageUrl ? 'filled' : 'empty'}>
              <span className="cw-file-row-icon"><ImageIcon size={13} /></span>
              <span className="cw-file-row-text">
                <span className="cw-file-row-title">{modelImageUrl ? 'Image uploaded' : 'Image (alternative to HTML model)'}</span>
              </span>
              {modelImageUrl ? (
                <span className="cw-file-row-actions">
                  <a href={portalHostedAssetHref(modelImageUrl)} target="_blank" rel="noopener noreferrer" className="cw-icon-btn" title="View full size"><Eye size={13} /></a>
                  <button className="cw-icon-btn" onClick={() => modelImageFileRef.current?.click()} disabled={uploadingModelImage} title="Replace">
                    {uploadingModelImage ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
                  </button>
                  <button className="cw-icon-btn danger" onClick={async () => {
                    if (!portalId) return;
                    const updates: Record<string, any> = { modelImageUrl: '', updatedAt: Timestamp.now() };
                    if (activeDealId) { updates[`projects.${activeDealId}.modelImageUrl`] = ''; updates[`projects.${activeDealId}.updatedAt`] = Timestamp.now(); }
                    await updateDoc(doc(db, 'clientPortals', portalId), updates);
                    setModelImageUrl('');
                    setPortal(prev => { if (!prev) return null; const updated = { ...prev, modelImageUrl: '' }; if (activeDealId && updated.projects?.[activeDealId]) { updated.projects = { ...updated.projects }; updated.projects[activeDealId] = { ...updated.projects[activeDealId], modelImageUrl: '' }; } return updated; });
                  }} title="Remove image"><Trash2 size={13} /></button>
                </span>
              ) : (
                <button className="cw-file-upload-btn" onClick={() => modelImageFileRef.current?.click()} disabled={uploadingModelImage}>
                  {uploadingModelImage ? <><Loader2 size={12} className="spin" /> Uploading…</> : <><Upload size={12} /> Upload</>}
                </button>
              )}
            </div>

            {/* Client notes + Loom, one save */}
            <div className="cw-form-stack">
              <span className="cw-form-label"><StickyNote size={12} /> Client notes</span>
              <textarea
                className="pm-textarea"
                value={modelNotes}
                onChange={(e) => setModelNotes(e.target.value)}
                placeholder="Notes visible to client..."
                rows={2}
              />
            </div>
            {portalId && (
              <PortalModelVersionLoomPanel
                portalId={portalId}
                releases={modelReleasesForStep}
                currentReleaseId={displayModelRelease?.id}
              />
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              {relatedDeal && relatedDeal.stageId === 'design' ? (
                <button type="button" className="cw-text-link" onClick={() => navigate('/design-tracker')}>
                  <PenLine size={12} /> Open Design Tracker
                </button>
              ) : <span />}
              <button
                type="button"
                className="cw-body-save"
                onClick={() => handleSaveStepDetails({ modelNotes })}
                disabled={saving}
              >
                <Save size={12} /> {saving ? 'Saving…' : 'Save notes'}
              </button>
            </div>

            {/* Walkthrough recorder (beta) */}
            <div className="cw-form-stack" style={{ marginTop: 8 }}>
              <span className="cw-form-label">
                <Video size={12} /> 3D walkthrough recorder <span className="pmwr-beta">Beta</span>
              </span>
              <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                Effective: {walkthroughEffectiveForProject ? 'On' : 'Off'}
                {walkthroughOverride === 'inherit'
                  ? ` (company default: ${globalWalkthroughDefault ? 'on' : 'off'})`
                  : walkthroughOverride === 'on'
                    ? ' (enabled for this project)'
                    : ' (disabled for this project)'}
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(['inherit', 'on', 'off'] as const).map(mode => (
                  <button
                    key={mode}
                    type="button"
                    className={`cw-mini-btn${walkthroughOverride === mode ? ' active' : ''}`}
                    disabled={savingWalkthroughToggle}
                    onClick={() => void saveWalkthroughProjectSetting(mode)}
                  >
                    {mode === 'inherit' ? 'Use default' : mode === 'on' ? 'Enable' : 'Disable'}
                  </button>
                ))}
              </div>
            </div>

            {portalId && activeDealId && (
              <PortalModelWalkthroughStaffPanel
                portalId={portalId}
                dealId={activeDealId}
                highlightWalkthroughId={walkthroughHighlightId}
              />
            )}

            {/* Review & approve — off until a quote is sent, unless staff keeps it off */}
            {(() => {
              const flowOn = isConceptApprovalFlowEnabled({
                flowDisabled: conceptApprovalFlowDisabled,
                proposalStatus: proposalBuilderData?.status,
              });
              const effectiveLabel = conceptApprovalFlowDisabled
                ? 'Turned off'
                : flowOn
                  ? 'On — clients review and approve before signing'
                  : 'Off until a quote is sent';
              return (
                <div className="cw-form-stack">
                  <label className="cw-toggle-row" data-on={conceptApprovalFlowDisabled}>
                    <input
                      type="checkbox"
                      checked={conceptApprovalFlowDisabled}
                      onChange={(e) => {
                        setConceptApprovalFlowDisabled(e.target.checked);
                        handleSaveStepDetails({ conceptApprovalFlowDisabled: e.target.checked });
                      }}
                    />
                    <span>Turn off review &amp; approve</span>
                  </label>
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    Review and approve stays off until a quote is sent. Check this to keep it off after the quote goes out.
                    {' '}
                    <strong>{effectiveLabel}</strong>
                  </p>
                </div>
              );
            })()}

            {/* Skip */}
            <label className="cw-toggle-row" data-tone="warn" data-on={modelStepSkipped}>
              <input type="checkbox" checked={modelStepSkipped} onChange={(e) => { setModelStepSkipped(e.target.checked); handleSaveStepDetails({ modelStepSkipped: e.target.checked }); }} />
              <span>Skip — not applicable</span>
            </label>
          </div>
        </div>
      </div>
    );
  };

  const handleSendProposal = async (customMessage: string, recipients: string[]) => {
    if (!relatedDeal?.id || !proposalBuilderData?.slug || !portal) return;
    setSendingProposal(true);
    try {
      const dealId = relatedDeal.id!;
      const nodeSets = proposalBuilderData.nodeSets || [];
      const nodeSetTotals = nodeSets.map((ns: any) => ({
        nodeSetId: ns.id,
        name: ns.name,
        total: ns.billablePrices?.subtotal || 0,
      }));
      const grandTotal = staffBillableTotalFromProposal(proposalBuilderData);
      const installTotal = nodeSets.reduce((sum: number, ns: any) => sum + (ns.billablePrices?.install || 0), 0);

      const lockedPricing: any = { grandTotal, installTotal, nodeSetTotals };
      if (proposalBuilderData.kitPricing) {
        lockedPricing.kitPricing = proposalBuilderData.kitPricing.map((kp: any) => ({
          speciesName: kp.speciesName,
          totalCost: kp.totalCost,
        }));
      }

      const proposalRef = doc(db, 'proposalBuilders', dealId);
      const designEstimating = lockDesignEstimating(proposalBuilderData.designEstimating);
      await setDoc(proposalRef, removeUndefinedDeep({
        status: 'sent',
        sentAt: new Date(),
        // Lock the public proposal slug — once shared with the client, the
        // URL must remain stable even if the project is later renamed.
        slugLocked: true,
        pricingLocked: true,
        pricingLockedAt: new Date(),
        lockedPricing,
        designEstimating,
      }), { merge: true });

      try {
        const mergedForSnapshot: ProposalBuilderData = {
          ...proposalBuilderData,
          id: dealId,
          dealId,
          status: 'sent',
          sentAt: new Date(),
          pricingLocked: true,
          pricingLockedAt: new Date(),
          lockedPricing,
          designEstimating,
        };
        await createProposalVersion(dealId, mergedForSnapshot, {
          source: 'sent',
          label: 'Sent to client',
          createdBy: user?.email || user?.id || 'system',
        });
      } catch (verErr) {
        console.warn('Failed to save proposal version snapshot:', verErr);
      }

      if (proposalBuilderData.slug) {
        const portalUpdates: Record<string, any> = {
          proposalSlug: proposalBuilderData.slug,
          dealId: dealId,
          updatedAt: Timestamp.now(),
          [`projects.${dealId}.proposalSlug`]: proposalBuilderData.slug,
          [`projects.${dealId}.updatedAt`]: Timestamp.now(),
        };
        await updateDoc(doc(db, 'clientPortals', portal.id!), portalUpdates);
        setPortal(prev => {
          if (!prev) return null;
          const updated = { ...prev, proposalSlug: proposalBuilderData.slug, dealId };
          if (updated.projects?.[dealId]) {
            updated.projects = { ...updated.projects };
            updated.projects[dealId] = { ...updated.projects[dealId], proposalSlug: proposalBuilderData.slug };
          }
          return updated;
        });
      }

      try {
        await createActivity({
          type: 'note',
          title: `Proposal sent: ${relatedDeal.title || 'Unknown'}`,
          description: `Proposal sent with value $${grandTotal.toLocaleString()}${proposalBuilderData.kitPricing ? ' (Kit Proposal)' : ''}`,
          entityType: 'deal',
          entityId: dealId,
          status: 'completed',
          dueDate: new Date(),
          completedAt: new Date(),
          assignedTo: '',
          createdBy: user?.id || 'system',
          actorType: 'employee',
          actorName: user?.name || user?.email || 'Employee',
        });
      } catch (e) { console.warn('Failed to log activity:', e); }

      try {
        await updateEnhancedDeal(dealId, { stageId: 'estimated', value: grandTotal } as any);
      } catch (e) { console.warn('Failed to move deal to estimated:', e); }

      // Fire off email
      try {
        const fnUrl = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-lctf-projects.cloudfunctions.net';
        await fetch(`${fnUrl}/sendProposalEmail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            portalId: portal.id,
            customMessage,
            recipients,
          }),
        });
      } catch (e) { console.warn('Failed to send proposal email:', e); }

      setProposalBuilderData((prev: any) => prev ? {
        ...prev,
        status: 'sent',
        pricingLocked: true,
        pricingLockedAt: new Date(),
        lockedPricing,
      } : null);

      // Re-fetch to ensure we have the latest state from Firestore
      try {
        const freshSnap = await getDoc(doc(db, 'proposalBuilders', dealId));
        if (freshSnap.exists()) {
          setProposalBuilderData({ id: freshSnap.id, ...freshSnap.data() });
        }
      } catch (e) { console.warn('Failed to re-fetch proposal data:', e); }

      setShowReviewModal(false);
    } catch (error) {
      console.error('Error sending proposal:', error);
      alert('Failed to send proposal. Please try again.');
    } finally {
      setSendingProposal(false);
    }
  };

  const handleMarkProposalSent = async () => {
    if (!relatedDeal?.id || !proposalBuilderData?.slug || !portal) return;
    if (!window.confirm('Mark this proposal as sent without sending an email? The client will see it in their portal.')) return;
    setSendingProposal(true);
    try {
      const dealId = relatedDeal.id!;
      const nodeSets = proposalBuilderData.nodeSets || [];
      const nodeSetTotals = nodeSets.map((ns: any) => ({
        nodeSetId: ns.id,
        name: ns.name,
        total: ns.billablePrices?.subtotal || 0,
      }));
      const grandTotal = staffBillableTotalFromProposal(proposalBuilderData);
      const installTotal = nodeSets.reduce((sum: number, ns: any) => sum + (ns.billablePrices?.install || 0), 0);

      const lockedPricing: any = { grandTotal, installTotal, nodeSetTotals };
      if (proposalBuilderData.kitPricing) {
        lockedPricing.kitPricing = proposalBuilderData.kitPricing.map((kp: any) => ({
          speciesName: kp.speciesName,
          totalCost: kp.totalCost,
        }));
      }

      const proposalRef = doc(db, 'proposalBuilders', dealId);
      const designEstimating = lockDesignEstimating(proposalBuilderData.designEstimating);
      await setDoc(proposalRef, removeUndefinedDeep({
        status: 'sent',
        sentAt: new Date(),
        // Lock the public proposal slug — once shared with the client, the
        // URL must remain stable even if the project is later renamed.
        slugLocked: true,
        pricingLocked: true,
        pricingLockedAt: new Date(),
        lockedPricing,
        designEstimating,
      }), { merge: true });

      if (proposalBuilderData.slug) {
        const portalUpdates: Record<string, any> = {
          proposalSlug: proposalBuilderData.slug,
          dealId: dealId,
          updatedAt: Timestamp.now(),
          [`projects.${dealId}.proposalSlug`]: proposalBuilderData.slug,
          [`projects.${dealId}.updatedAt`]: Timestamp.now(),
        };
        await updateDoc(doc(db, 'clientPortals', portal.id!), portalUpdates);
        setPortal(prev => {
          if (!prev) return null;
          const updated = { ...prev, proposalSlug: proposalBuilderData.slug, dealId };
          if (updated.projects?.[dealId]) {
            updated.projects = { ...updated.projects };
            updated.projects[dealId] = { ...updated.projects[dealId], proposalSlug: proposalBuilderData.slug };
          }
          return updated;
        });
      }

      try {
        await createActivity({
          type: 'note',
          title: `Proposal marked as sent: ${relatedDeal.title || 'Unknown'}`,
          description: `Proposal marked as sent (no email) with value $${grandTotal.toLocaleString()}${proposalBuilderData.kitPricing ? ' (Kit Proposal)' : ''}`,
          entityType: 'deal',
          entityId: dealId,
          status: 'completed',
          dueDate: new Date(),
          completedAt: new Date(),
          assignedTo: '',
          createdBy: user?.id || 'system',
          actorType: 'employee',
          actorName: user?.name || user?.email || 'Employee',
        });
      } catch (e) { console.warn('Failed to log activity:', e); }

      try {
        await updateEnhancedDeal(dealId, { stageId: 'estimated', value: grandTotal } as any);
      } catch (e) { console.warn('Failed to move deal to estimated:', e); }

      setProposalBuilderData((prev: any) => prev ? {
        ...prev,
        status: 'sent',
        pricingLocked: true,
        pricingLockedAt: new Date(),
        lockedPricing,
      } : null);

      try {
        const freshSnap = await getDoc(doc(db, 'proposalBuilders', dealId));
        if (freshSnap.exists()) {
          setProposalBuilderData({ id: freshSnap.id, ...freshSnap.data() });
        }
      } catch (e) { console.warn('Failed to re-fetch proposal data:', e); }
    } catch (error) {
      console.error('Error marking proposal as sent:', error);
      alert('Failed to mark proposal as sent. Please try again.');
    } finally {
      setSendingProposal(false);
    }
  };

  const handleUnsendProposal = async () => {
    if (!relatedDeal?.id) return;
    if (!window.confirm('Unsend this proposal? Pricing will be unlocked and the client will no longer see the proposal in their portal.')) return;
    setSaving(true);
    try {
      const dealId = relatedDeal.id!;
      const proposalRef = doc(db, 'proposalBuilders', dealId);
      await setDoc(proposalRef, {
        status: 'draft',
        pricingLocked: false,
        pricingLockedAt: null,
        lockedPricing: null,
      }, { merge: true });

      setProposalBuilderData((prev: any) => prev ? {
        ...prev,
        status: 'draft',
        pricingLocked: false,
        pricingLockedAt: undefined,
        lockedPricing: undefined,
      } : null);

      try {
        await createActivity({
          type: 'note',
          title: `Proposal unsent: ${relatedDeal.title || 'Unknown'}`,
          description: 'Proposal reverted to draft. Pricing unlocked.',
          entityType: 'deal',
          entityId: dealId,
          status: 'completed',
          dueDate: new Date(),
          completedAt: new Date(),
          assignedTo: '',
          createdBy: user?.id || 'system',
          actorType: 'employee',
          actorName: user?.name || user?.email || 'Employee',
        });
      } catch (e) { console.warn('Failed to log unsend activity:', e); }
    } catch (error) {
      console.error('Error unsending proposal:', error);
      alert('Failed to unsend proposal. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSignedContract = async () => {
    if (!activeDealId || generatingSignedContract) return;
    if (hasClientPortalSignature(proposalBuilderData?.signature)) {
      alert('This proposal already has a client signature. Use the signed PDF from the public proposal.');
      return;
    }
    setGeneratingSignedContract(true);
    try {
      const result = await applyInternalClientDirectionSign(activeDealId);
      if (!result.ok) {
        alert(result.error);
        return;
      }
      await refreshProposalBuilder(activeDealId);
    } catch (err) {
      console.error('[ClientPortal] generate signed contract:', err);
      alert('Failed to generate signed contract. Please try again.');
    } finally {
      setGeneratingSignedContract(false);
    }
  };

  const handleRevertToQuote = async () => {
    if (!portalId || !portal) return;
    if (!window.confirm('Revert to the Quote stage? The proposal will be reset to "sent" status and any signature data will be cleared.')) return;

    // Route the stage write through handleRevertStage so stage history is
    // truncated, the manual-override pin is set, and local state updates —
    // the old direct write left the timeline showing stale green steps.
    await handleRevertStage('quote');

    setSaving(true);
    try {
      if (relatedDeal?.id) {
        const proposalRef = doc(db, 'proposalBuilders', relatedDeal.id);
        await setDoc(proposalRef, {
          status: 'sent',
          signature: null,
          signedPdfUrl: null,
          approvedAt: null,
        }, { merge: true });

        setProposalBuilderData((prev: any) => prev ? {
          ...prev,
          status: 'sent',
          signature: undefined,
          signedPdfUrl: undefined,
          approvedAt: undefined,
        } : null);
      }

      setSelectedStep('quote');

      try {
        await createActivity({
          type: 'note',
          title: `Proposal reverted: ${relatedDeal?.title || 'Unknown'}`,
          description: 'Proposal reverted from signed back to quote stage. Signature data cleared.',
          entityType: 'deal',
          entityId: relatedDeal?.id || portalId,
          status: 'completed',
          dueDate: new Date(),
          completedAt: new Date(),
          assignedTo: '',
          createdBy: user?.id || 'system',
          actorType: 'employee',
          actorName: user?.name || user?.email || 'Employee',
        });
      } catch (e) { console.warn('Failed to log revert activity:', e); }
    } catch (error) {
      console.error('Error reverting to quote:', error);
      alert('Failed to revert. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddProject = async () => {
    if (!lead || !leadId) return;
    if (!deferProjectNumber && !isProjectNumber(newProjectNaming.projectNumber)) {
      setNewProjectError('Waiting for project number preview. Please wait a moment and try again.');
      return;
    }
    if (!newProjectNaming.clientLabel.trim() || !newProjectNaming.descriptor.trim()) {
      setNewProjectError('Client Label and Descriptor are required.');
      return;
    }
    setNewProjectError(null);
    setAddingProject(true);
    try {
      const defaultPipeline = pipelines.find(p => p.isDefault) || pipelines[0];
      if (!defaultPipeline?.id) throw new Error('No pipeline found');

      const clientLabel = newProjectNaming.clientLabel.trim();
      const descriptor = newProjectNaming.descriptor.trim();
      const legacyShortName = descriptor;

      let reservedProjectNumber: string | undefined;
      if (!deferProjectNumber) {
        // Reserve the actual project number atomically NOW (deferred from
        // dialog mount so cancelling never burns a counter slot). The peeked
        // value in state may differ from the reserved one if another user
        // created a project between preview and submit.
        reservedProjectNumber = await reserveProjectNumberOnSubmit();
        setNewProjectNaming(prev => ({ ...prev, projectNumber: reservedProjectNumber! }));
      }

      const dealId = await createProjectForLead(
        lead,
        legacyShortName,
        lead.projectType || 'Custom',
        defaultPipeline.id,
        user?.id || 'system',
        undefined,
        {
          ...(reservedProjectNumber ? { projectNumber: reservedProjectNumber } : {}),
          clientLabel,
          descriptor,
        },
      );

      const canonicalName = getProjectDisplayName({
        projectNumber: reservedProjectNumber,
        clientLabel,
        descriptor,
        title: legacyShortName,
      });

      // Initialize a project entry in the portal
      if (portalId) {
        const newProject: Record<string, any> = {
          dealId,
          projectName: canonicalName,
          clientLabel,
          descriptor,
          currentStage: 'design-fee',
          stageHistory: [{ stage: 'design-fee', enteredAt: Timestamp.now() }],
          designFeeSkipped: false,
          hiddenFromPortal: false,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };
        if (reservedProjectNumber) {
          newProject.projectNumber = reservedProjectNumber;
        }
        await updateDoc(doc(db, 'clientPortals', portalId), {
          [`projects.${dealId}`]: newProject,
          updatedAt: Timestamp.now(),
        });
        setPortal(prev => {
          if (!prev) return null;
          return {
            ...prev,
            projects: {
              ...prev.projects,
              [dealId]: {
                ...newProject,
                stageHistory: [{ stage: 'design-fee' as PortalStage, enteredAt: new Date() }],
                createdAt: new Date(),
                updatedAt: new Date(),
              } as PortalProject,
            },
          };
        });
      }

      setActiveDealId(dealId);
      setShowAddProjectModal(false);
      setDeferProjectNumber(false);
      setNewProjectNaming({ projectNumber: '', clientLabel: '', descriptor: '' });
      setNewProjectError(null);
    } catch (err) {
      console.error('Error adding project:', err);
      alert('Failed to add project');
    } finally {
      setAddingProject(false);
    }
  };

  // Reset/seed naming state whenever the Add Project modal is opened.
  useEffect(() => {
    if (showAddProjectModal) {
      setNewProjectNaming({
        projectNumber: '',
        clientLabel: defaultClientLabelFromLead(lead),
        descriptor: defaultDescriptorFromProjectType(lead?.projectType),
      });
      setDeferProjectNumber(false);
      setNewProjectError(null);
    }
  }, [showAddProjectModal, lead]);

  const handleStartRename = (deal: { id?: string }, opts?: { assignNumber?: boolean }) => {
    if (!deal.id) return;
    setRenamingDealId(deal.id);
    setRenamingAssignNumber(!!opts?.assignNumber);
  };

  const handleCancelRename = () => {
    setRenamingDealId(null);
    setRenamingAssignNumber(false);
  };

  /**
   * Confirmed rename — writes the canonical naming fields plus the legacy
   * `title` mirror, and optimistically updates the portal projection so the
   * UI reflects the change immediately. The Cloud Function trigger picks up
   * the rest (timberProjects, matchedDealTitle caches, etc.).
   */
  const handleConfirmRename = async (
    dealId: string,
    next: { projectNumber: string; clientLabel: string; descriptor: string },
  ) => {
    if (!lead) return;
    setSaving(true);
    try {
      const canonicalName = [next.projectNumber, next.clientLabel.trim(), next.descriptor.trim()].filter(Boolean).join(' ').trim();
      const trimmedLabel = next.clientLabel.trim();
      const trimmedDescriptor = next.descriptor.trim();
      const trimmedNumber = next.projectNumber.trim();
      await updateEnhancedDeal(dealId, {
        ...(trimmedNumber ? { projectNumber: trimmedNumber } : {}),
        clientLabel: trimmedLabel,
        descriptor: trimmedDescriptor,
        title: canonicalName,
        projectName: trimmedDescriptor,
      });
      if (portalId) {
        await updateDoc(doc(db, 'clientPortals', portalId), {
          [`projects.${dealId}.projectName`]: canonicalName,
          ...(trimmedNumber ? { [`projects.${dealId}.projectNumber`]: trimmedNumber } : {}),
          [`projects.${dealId}.clientLabel`]: trimmedLabel,
          [`projects.${dealId}.descriptor`]: trimmedDescriptor,
          [`projects.${dealId}.updatedAt`]: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        setPortal(prev => {
          if (!prev) return prev;
          const existing = prev.projects?.[dealId] ?? getProjectFromPortal(prev, dealId);
          if (!existing && prev.dealId !== dealId) return prev;
          const base = existing || {
            dealId,
            projectName: '',
            currentStage: 'design-fee' as PortalStage,
            stageHistory: [],
            designFeeSkipped: false,
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          return {
            ...prev,
            projects: {
              ...(prev.projects || {}),
              [dealId]: {
                ...base,
                projectName: canonicalName,
                ...(trimmedNumber ? { projectNumber: trimmedNumber } : {}),
                clientLabel: trimmedLabel,
                descriptor: trimmedDescriptor,
                updatedAt: new Date(),
              } as PortalProject,
            },
          };
        });
      }
      setRenamingDealId(null);
      setRenamingAssignNumber(false);
    } catch (err) {
      console.error('Error renaming project:', err);
      alert('Failed to rename project');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleEditLeadName = () => {
    if (!lead) return;
    setEditFullName(`${lead.firstName || ''} ${lead.lastName || ''}`.trim());
    setEditingLeadName(true);
  };

  const handleSaveLeadName = async () => {
    if (!lead?.id) return;
    const parts = editFullName.trim().split(/\s+/);
    const newFirstName = parts[0] || lead.firstName || '';
    const newLastName = parts.slice(1).join(' ') || lead.lastName || '';
    if (!newFirstName && !newLastName) {
      setEditingLeadName(false);
      return;
    }
    setSaving(true);
    try {
      await updateLead(lead.id, { firstName: newFirstName, lastName: newLastName });

      const newClientName = `${newFirstName} ${newLastName}`.trim();
      if (portalId) {
        await updateDoc(doc(db, 'clientPortals', portalId), {
          clientName: newClientName,
          updatedAt: Timestamp.now(),
        });
        setPortal(prev => prev ? { ...prev, clientName: newClientName } : null);
      }

      // Two strategies depending on whether the deal has been migrated to
      // the canonical naming triple:
      //
      //   1. MIGRATED — update `clientLabel` (recomputed from the new lead
      //      name) and rebuild `title` via `getProjectDisplayName`. We do
      //      NOT touch `descriptor` or `projectNumber`. The trigger fans
      //      out the new name to every projection.
      //
      //   2. LEGACY — preserve the historical "First Last - Project" title
      //      template so older projections (that still read `title`
      //      verbatim) continue to make sense. The migration wizard is the
      //      surface for moving these onto the new schema.
      const updatedLead = { ...lead, firstName: newFirstName, lastName: newLastName };
      for (const deal of relatedDeals) {
        if (!deal.id) continue;
        if (isLegacyDealName(deal)) {
          const proj = portal ? (portal.projects?.[deal.id] ?? getProjectFromPortal(portal, deal.id)) : null;
          const projName = proj?.projectName
            || (deal.title?.includes(' - ') ? deal.title.split(' - ').slice(1).join(' - ') : '')
            || deal.title
            || '';
          const newTitle = projName ? `${newFirstName} ${newLastName} - ${projName}` : `${newFirstName} ${newLastName}`;
          await updateEnhancedDeal(deal.id, { title: newTitle, contactName: newClientName });
        } else {
          const nextLabel = defaultClientLabelFromLead(updatedLead);
          const newTitle = getProjectDisplayName({
            projectNumber: deal.projectNumber,
            clientLabel: nextLabel,
            descriptor: deal.descriptor,
            title: deal.title,
          });
          await updateEnhancedDeal(deal.id, {
            clientLabel: nextLabel,
            title: newTitle,
            contactName: newClientName,
          });
        }
      }

      try {
        await syncProposalClientIdentityForDeals(
          relatedDeals.map(d => d.id).filter(Boolean) as string[],
          {
            clientName: newClientName,
            leadCompany: lead.company || '',
          },
        );
      } catch (syncErr) {
        console.warn('[ClientPortal] proposal client identity sync failed:', syncErr);
      }

      setEditingLeadName(false);
    } catch (err) {
      console.error('Error saving lead name:', err);
      alert('Failed to save name');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEditLeadName = () => {
    setEditingLeadName(false);
    setEditFullName('');
  };

  const handleTogglePortalVisibility = async (dealId: string) => {
    if (!portalId || !portal) return;
    const proj = portal.projects?.[dealId] ?? getProjectFromPortal(portal, dealId);
    const nextHidden = !(proj?.hiddenFromPortal ?? false);
    setSaving(true);
    try {
      await updateDoc(doc(db, 'clientPortals', portalId), {
        [`projects.${dealId}.hiddenFromPortal`]: nextHidden,
        [`projects.${dealId}.updatedAt`]: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      setPortal(prev => {
        if (!prev) return null;
        const updated = { ...prev };
        if (!updated.projects) updated.projects = {};
        updated.projects = { ...updated.projects };
        const existing = updated.projects[dealId] ?? getProjectFromPortal(prev, dealId);
        updated.projects[dealId] = {
          ...(existing || { dealId, projectName: '', currentStage: 'design-fee' as PortalStage, stageHistory: [], designFeeSkipped: false, createdAt: new Date(), updatedAt: new Date() }),
          hiddenFromPortal: nextHidden,
          updatedAt: new Date(),
        } as PortalProject;
        return updated;
      });
    } catch (err) {
      console.error('Error toggling portal visibility:', err);
      alert('Failed to update visibility');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async (dealId: string, dealName: string) => {
    if (!portalId || !portal) return;
    if (!window.confirm(`Delete project "${dealName}"?\n\nThis will remove the project and its deal record permanently.`)) return;
    setSaving(true);
    try {
      // Remove from portal projects map
      await updateDoc(doc(db, 'clientPortals', portalId), {
        [`projects.${dealId}`]: deleteField(),
        updatedAt: Timestamp.now(),
      });

      // Delete the deal (and any associated proposal) — onSnapshot will remove it from relatedDeals
      await deleteEnhancedDeal(dealId);

      setPortal(prev => {
        if (!prev?.projects) return prev;
        const updated = { ...prev, projects: { ...prev.projects } };
        delete updated.projects[dealId];
        return updated;
      });
      if (activeDealId === dealId) {
        const remaining = relatedDeals.filter(d => d.id !== dealId);
        const nextPreferred =
          portal && remaining.length
            ? remaining.find(r => r.id && !getProjectFromPortal(portal, r.id)?.hiddenFromPortal) ?? remaining[0]
            : remaining[0];
        setActiveDealId(nextPreferred?.id || null);
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project');
    } finally {
      setSaving(false);
    }
  };

  const renderStepQuote = () => {
    const proposalStatus_ = proposalBuilderData?.status;
    const isSigned = proposalStatus_ === 'approved';
    // A proposal counts as "sent" once it leaves the draft state for any
    // reason. The status auto-advances 'sent' → 'opened' the first time the
    // client views the public proposal page; before this fix the UI treated
    // 'opened' as a fresh draft and re-offered "Review & Send" / "Mark as
    // sent" after a reload, even though the proposal was locked server-side.
    const isSent = proposalStatus_ === 'sent' || proposalStatus_ === 'opened';
    const isDraft = !isSent && !isSigned && proposalStatus_ !== 'rejected';

    return (
      <div className="pm-detail-card">
        <div className="pm-detail-header">
          <ClipboardList size={20} />
          <div>
            <h3>Your Quote</h3>
            <p>Build and send the project proposal.</p>
          </div>
          <div className="pm-header-right">
            {proposalBuilderData?.status && (
              <span className={`pm-detail-badge pm-badge-${proposalBuilderData.status}`}>
                {proposalBuilderData.status.charAt(0).toUpperCase() + proposalBuilderData.status.slice(1)}
              </span>
            )}
            {relatedDeal && (
              <div className="pm-quote-header-actions">
                <button className="pm-quote-action-sm" onClick={() => navigate(`/proposal-builder/${relatedDeal.id}`)}>
                  <FileEdit size={12} />
                  {proposalBuilderData ? 'Edit Proposal' : 'Build Proposal'}
                </button>
                {proposalBuilderData && (
                  <button className="pm-quote-action-sm pm-quote-action-activity" onClick={() => setShowActivityTracker(true)}>
                    <Activity size={12} />
                    Activity
                  </button>
                )}
                {proposalBuilderData && isDraft && proposalBuilderData.slug && (
                  <>
                    <button className="pm-quote-action-sm pm-quote-action-send" onClick={() => setShowReviewModal(true)}>
                      <Send size={12} />
                      Review & Send
                    </button>
                    <button className="pm-quote-action-sm pm-quote-action-mark-sent" onClick={handleMarkProposalSent} disabled={sendingProposal}>
                      {sendingProposal ? 'Saving...' : 'Mark as sent'}
                    </button>
                  </>
                )}
                {proposalBuilderData && isSent && !isSigned && (
                  <button className="pm-quote-action-sm pm-quote-action-unsend" onClick={handleUnsendProposal} disabled={saving}>
                    <Unlock size={11} />
                    {saving ? 'Unsending...' : 'Unsend & Unlock'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {opsReviewMeta?.status === 'pending' && (
          <div
            className="pm-detail-section"
            style={{
              margin: '8px 16px 0',
              padding: '10px 12px',
              background: 'var(--color-warning-light)',
              border: '1px solid var(--color-warning)',
              borderRadius: 8,
              fontSize: 13,
              color: 'var(--color-warning-text)',
            }}
            role="status"
          >
            OPS review in progress — waiting for {opsReviewMeta.reviewerName || 'PM'} to review. You can still send when ready.
          </div>
        )}

        <div className="pm-detail-body">
          <div className="cw-stack">
          {/* Key facts — compact OPS strip instead of the old half-width table */}
          {relatedDeal ? (
            <div className="cw-facts">
              <div className="cw-fact">
                <span className="cw-fact-label">Deal</span>
                <span className="cw-fact-value" title={relatedDeal.title}>{relatedDeal.title}</span>
              </div>
              <div className="cw-fact">
                <span className="cw-fact-label">Value</span>
                <span className="cw-fact-value cw-fact-strong">${quoteDisplayTotal.toLocaleString()}</span>
              </div>
              <div className="cw-fact">
                <span className="cw-fact-label">Stage</span>
                <span className="cw-fact-value">
                  <span className="pm-stage-chip">{dealStageName}</span>
                  {relatedDeal.outcome === 'won' && <span className="pm-outcome-badge pm-outcome-won">Won</span>}
                  {relatedDeal.outcome === 'lost' && <span className="pm-outcome-badge pm-outcome-lost">Lost</span>}
                </span>
              </div>
              {activeProject?.targetCompletionDate && (
                <div className="cw-fact">
                  <span className="cw-fact-label">Client deadline</span>
                  <span className="cw-fact-value cw-fact-strong">
                    {new Date(activeProject.targetCompletionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="pm-detail-section pm-empty-state" style={{ margin: 0 }}>
              <AlertCircle size={16} />
              <p style={{ fontSize: 11 }}>A deal will be auto-created with the engagement letter.</p>
            </div>
          )}

          {/* Client-facing settings — one compact row; the popup hosts the
              timeline editor and the public-proposal timber-sizes toggle. */}
          <div className="cw-settings-row">
            <Settings2 size={14} />
            <div className="cw-settings-text">
              <span className="cw-settings-title">Client-Facing Settings</span>
              <span className="cw-settings-summary">
                Timeline <strong>~{timelineOverrides.reduce((s, p) => s + p.durationWeeks + p.bufferWeeks, 0)} weeks</strong>
                {' · '}Timber sizes <strong>{proposalBuilderData?.publicSettings?.showTimberSizes ? 'On' : 'Off'}</strong>
                {' · '}$/sf <strong>{proposalBuilderData?.publicSettings?.showCostPerSqFt ? 'On' : 'Off'}</strong>
              </span>
            </div>
            {timelineSaved && <span className="cw-settings-saved">Saved</span>}
            <button
              type="button"
              className="cw-settings-btn"
              onClick={() => setPublicSettingsOpen(true)}
              disabled={!activeDealId}
              title={activeDealId ? 'Edit timeline phases and public-proposal toggles' : 'No active deal'}
            >
              <Settings2 size={12} />
              Edit Settings
            </button>
          </div>

          {/* Phased projects (beta): phase status + reprice / offer / internal-commit */}
          {activeDealId && proposalBuilderData && user?.id && (
            <PhaseManagementPanel
              proposalId={activeDealId}
              proposal={proposalBuilderData as ProposalBuilderData}
              dealId={activeDealId}
              projectId={timberProjectForActiveDeal?.id}
              userId={user.id}
              userName={user.name || user.email || 'Staff'}
              isAdmin={user.role === 'admin'}
              onProposalUpdated={(fresh) => setProposalBuilderData(fresh)}
            />
          )}
          </div>
        </div>

        {/* Review & Send Modal */}
        {showReviewModal && proposalBuilderData && portal && (
          <ProposalReviewModal
            open={showReviewModal}
            onClose={() => setShowReviewModal(false)}
            onSend={handleSendProposal}
            clientName={portal.clientName || (lead ? `${lead.firstName} ${lead.lastName}`.trim() : 'Client')}
            clientEmail={portal.clientEmail || lead?.email || ''}
            proposalSlug={proposalBuilderData.slug}
            nodeSets={proposalBuilderData.nodeSets || []}
            profitMargin={proposalBuilderData.profitMargin || 0.25}
            sending={sendingProposal}
            savedMessage={proposalEmailMessage}
            onMessageChange={setProposalEmailMessage}
            savedRecipients={proposalEmailRecipients}
            onRecipientsChange={setProposalEmailRecipients}
            globalCosts={proposalBuilderData.globalCosts}
            isKitProposal={proposalBuilderData.isKitProposal}
            kitPricing={proposalBuilderData.kitPricing}
            discount={proposalBuilderData.discount}
            snapshotProposal={proposalBuilderData}
            activeSpecies={
              proposalBuilderData.activeSpecies ||
              proposalBuilderData.offeredSpecies?.[0] ||
              proposalBuilderData.kitPricing?.[0]?.speciesName
            }
          />
        )}

        {showActivityTracker && relatedDeal?.id && (
          <ProposalActivityTracker
            dealId={relatedDeal.id}
            proposalId={proposalBuilderData?.id}
            clientName={portal?.clientName || (lead ? `${lead.firstName} ${lead.lastName}`.trim() : 'Client')}
            onClose={() => setShowActivityTracker(false)}
          />
        )}

        {/* Unified Public Proposal Settings popup. Re-mounted per open so the
            internal draft snapshot reseeds from current persisted values. */}
        {publicSettingsOpen && (
          <PublicProposalSettingsModal
            open={publicSettingsOpen}
            onClose={() => setPublicSettingsOpen(false)}
            portalId={portal?.id ?? null}
            dealId={activeDealId}
            initialTimelinePhases={timelineOverrides as PpsTLPhase[]}
            initialShowTimberSizes={!!proposalBuilderData?.publicSettings?.showTimberSizes}
            initialShowCostPerSqFt={!!proposalBuilderData?.publicSettings?.showCostPerSqFt}
            hasProposalDoc={!!proposalBuilderData}
            onSaved={({ phases, showTimberSizes, showCostPerSqFt }) => {
              // Mirror the saved values into local state so the page reflects
              // them immediately without refetching. Timeline state shape is
              // a superset of TLPhase, but we only edit the same fields.
              setTimelineOverrides(phases as typeof DEFAULT_TL_PHASES);
              setProposalBuilderData((prev: any) => prev
                ? {
                    ...prev,
                    publicSettings: {
                      ...(prev.publicSettings || {}),
                      showTimberSizes: !!showTimberSizes,
                      showCostPerSqFt: !!showCostPerSqFt,
                    },
                  }
                : prev);
              setTimelineSaved(true);
              setTimeout(() => setTimelineSaved(false), 2000);
            }}
          />
        )}
      </div>
    );
  };

  const renderStepQuoteSigned = () => {
    const signedAtRaw = proposalBuilderData?.signedAt;
    const signedAt = signedAtRaw
      ? new Date(signedAtRaw.toDate ? signedAtRaw.toDate() : signedAtRaw)
      : null;
    return (
      <div className="pm-detail-card">
        <div className="pm-detail-header">
          <PenTool size={20} />
          <div>
            <h3>Quote Signed</h3>
            <p>Contract execution and signature confirmation.</p>
          </div>
          <div className="pm-header-right">
            {(proposalBuilderData?.signedPdfUrl || storedPortalStage === 'quote-signed') && (
              <span className="pm-detail-badge pm-badge-signed">{proposalBuilderData?.signedPdfUrl ? 'Signed' : 'Signed (stage)'}</span>
            )}
          </div>
        </div>

        <div className="pm-detail-body">
          <div className="cw-stack">
            {proposalBuilderData?.signedPdfUrl ? (
              <>
                <div className="cw-facts">
                  <div className="cw-fact">
                    <span className="cw-fact-label">Status</span>
                    <span className="cw-fact-value cw-fact-strong">Contract signed</span>
                  </div>
                  {signedAt && (
                    <div className="cw-fact">
                      <span className="cw-fact-label">Signed</span>
                      <span className="cw-fact-value">
                        {signedAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                  <div className="cw-fact">
                    <span className="cw-fact-label">Document</span>
                    <span className="cw-fact-value">
                      <a href={proposalBuilderData.signedPdfUrl} target="_blank" rel="noopener noreferrer" className="cw-text-link">
                        <Download size={12} /> View Signed PDF
                      </a>
                    </span>
                  </div>
                </div>
                {/* Unlike the footer's plain Retreat, this also resets the
                    proposal to "sent" and clears the signature data. */}
                <button
                  type="button"
                  className="cw-text-link"
                  style={{ color: 'var(--color-warning-text)' }}
                  onClick={handleRevertToQuote}
                  disabled={saving}
                  title="Resets the proposal to sent, clears the signature, and retreats the stage to Quote"
                >
                  <RotateCcw size={11} /> {saving ? 'Reverting…' : 'Clear signature & revert to Quote'}
                </button>
              </>
            ) : storedPortalStage === 'quote-signed' ? (
              <div className="cw-stack" style={{ gap: '0.75rem' }}>
                <div className="cw-empty-line">
                  <PenTool size={14} />
                  <span>
                    The portal was advanced to <strong>Signed</strong> without a client signature — finish the
                    internal sign-off (signature: <em>{CLIENT_DIRECTION_SIGNATURE_LABEL}</em>).
                  </span>
                </div>
                {activeDealId && !hasClientPortalSignature(proposalBuilderData?.signature) && (
                  <button
                    type="button"
                    className="cw-mini-btn"
                    onClick={() => void handleGenerateSignedContract()}
                    disabled={generatingSignedContract || saving}
                  >
                    {generatingSignedContract ? (
                      <Loader2 size={11} className="pm-spin" />
                    ) : (
                      <FileSignature size={11} />
                    )}
                    {generatingSignedContract ? 'Generating contract…' : 'Complete internal sign-off'}
                  </button>
                )}
              </div>
            ) : (
              <div className="cw-empty-line">
                <Clock size={14} />
                Waiting for the client to sign. Send the quote from the previous step and the signed PDF will appear here.
              </div>
            )}

            {/* Phased projects (beta): later phases are offered/committed from here after signing */}
            {activeDealId && proposalBuilderData && user?.id && (
              <PhaseManagementPanel
                proposalId={activeDealId}
                proposal={proposalBuilderData as ProposalBuilderData}
                dealId={activeDealId}
                projectId={timberProjectForActiveDeal?.id}
                userId={user.id}
                userName={user.name || user.email || 'Staff'}
                isAdmin={user.role === 'admin'}
                onProposalUpdated={(fresh) => setProposalBuilderData(fresh)}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStep2DPlans = () => {
    return (
      <div className="pm-detail-card">
        <div className="pm-detail-header">
          <FileText size={20} />
          <div>
            <h3>2D Plans</h3>
            <p>Upload construction drawings for the client.</p>
          </div>
          {effectivePlansUrl && <span className="pm-detail-badge pm-badge-paid">{plansFromHubOnly ? 'Released' : 'Uploaded'}</span>}
          {activeProject?.plansApproval?.status === 'approved' && (
            <>
              <span className="pm-detail-badge pm-badge-paid" title={formatApprovalBadge(activeProject.plansApproval) || undefined}>
                Client approved
              </span>
              <button
                type="button"
                className="cw-icon-btn"
                title="Re-send Slack & CRM notifications for engineered drawings approval"
                disabled={resendingApprovalNotify === 'plans'}
                onClick={() => void handleResendApprovalNotifications('plans')}
              >
                {resendingApprovalNotify === 'plans' ? (
                  <Loader2 size={11} className="spin" />
                ) : (
                  <RefreshCw size={11} />
                )}
              </button>
            </>
          )}
        </div>

        <div className="pm-detail-body">
          <div className="cw-stack">
            <input
              ref={plansFileRef}
              type="file"
              accept=".pdf,.dwg,.dxf,.zip"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file, 'plans');
              }}
              style={{ display: 'none' }}
            />
            <div className="cw-file-row" data-state={effectivePlansUrl ? 'filled' : 'empty'}>
              <span className="cw-file-row-icon"><FileText size={13} /></span>
              <span className="cw-file-row-text">
                <span className="cw-file-row-title">
                  {effectivePlansUrl
                    ? (plansFromHubOnly ? 'Plans — released from Project Hub' : 'Plans uploaded')
                    : 'Construction drawings (.pdf, .dwg, .dxf, .zip)'}
                </span>
                {displayPlansRelease && (
                  <span className="cw-file-row-sub" title={displayPlansRelease.name}>
                    {displayPlansRelease.sourceStreamTitle || 'Engineered Drawings'}
                    {displayPlansRelease.versionLabel ? ` · ${displayPlansRelease.versionLabel}` : ''}
                    {displayPlansRelease.versionIndex != null ? ` · #${displayPlansRelease.versionIndex}` : ''}
                    {displayPlansRelease.releaseVersionIndex != null ? ` · v${displayPlansRelease.releaseVersionIndex}` : ''}
                    {displayPlansRelease.sourceProjectId && displayPlansRelease.sourceStreamId && (
                      <Link to={`/projects/${displayPlansRelease.sourceProjectId}?tab=files&track=${displayPlansRelease.sourceStreamId}`}>
                        Open in Hub <ExternalLink size={9} aria-hidden />
                      </Link>
                    )}
                  </span>
                )}
              </span>
              {effectivePlansUrl ? (
                <span className="cw-file-row-actions">
                  <a href={portalHostedAssetHref(effectivePlansUrl, { downloadName: 'Plans' })} target="_blank" rel="noopener noreferrer" className="cw-icon-btn" title="Download">
                    <Download size={13} />
                  </a>
                  {!plansFromHubOnly && (
                    <button className="cw-icon-btn" onClick={() => plansFileRef.current?.click()} disabled={uploadingPlans} title="Replace">
                      {uploadingPlans ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
                    </button>
                  )}
                </span>
              ) : (
                <button className="cw-file-upload-btn" onClick={() => plansFileRef.current?.click()} disabled={uploadingPlans}>
                  {uploadingPlans ? <><Loader2 size={12} className="spin" /> Uploading…</> : <><Upload size={12} /> Upload</>}
                </button>
              )}
            </div>

            {relatedDeal && relatedDeal.stageId === 'design' && (
              <button type="button" className="cw-text-link" onClick={() => navigate('/design-tracker')}>
                <PenLine size={12} /> Open Design Tracker
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStepProduction = () => {
    return (
      <div className="pm-detail-card">
        <div className="pm-detail-header">
          <Factory size={20} />
          <div>
            <h3>In Production</h3>
            <p>Track manufacturing progress.</p>
          </div>
          <span className={`pm-detail-badge pm-prod-${productionStatus}`}>
            {productionStatus === 'in-progress' ? 'In Progress' : productionStatus.charAt(0).toUpperCase() + productionStatus.slice(1)}
          </span>
        </div>

        <div className="pm-detail-body">
          <div className="cw-stack">
            <div className="cw-facts">
              <div className="cw-fact">
                <span className="cw-fact-label">Status</span>
                <span className="cw-fact-value cw-fact-strong">
                  {productionStatus === 'in-progress' ? 'In Progress' : productionStatus.charAt(0).toUpperCase() + productionStatus.slice(1)}
                </span>
              </div>
              <div className="cw-fact">
                <span className="cw-fact-label">Est. completion</span>
                <span className="cw-fact-value">
                  {estimatedCompletion
                    ? new Date(estimatedCompletion).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—'}
                </span>
              </div>
            </div>

            <div className="cw-form-row">
              <span className="cw-form-label">Status</span>
              <select className="pm-select" value={productionStatus} onChange={(e) => setProductionStatus(e.target.value as any)}>
                <option value="queued">Queued</option>
                <option value="in-progress">In Progress</option>
                <option value="complete">Complete</option>
              </select>
            </div>
            <div className="cw-form-row">
              <span className="cw-form-label">Est. completion</span>
              <input type="date" className="pm-input" value={estimatedCompletion} onChange={(e) => setEstimatedCompletion(e.target.value)} />
            </div>
            <div className="cw-form-stack">
              <span className="cw-form-label">Production notes (internal)</span>
              <textarea className="pm-textarea" value={productionNotes} onChange={(e) => setProductionNotes(e.target.value)} placeholder="Internal production notes..." rows={3} />
            </div>

            <button
              type="button"
              className="cw-body-save"
              onClick={() => handleSaveStepDetails({
                productionStatus,
                estimatedCompletion: estimatedCompletion ? Timestamp.fromDate(new Date(estimatedCompletion)) : null,
                productionNotes,
              })}
              disabled={saving}
            >
              <Save size={12} /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStepDelivery = () => {
    return (
      <div className="pm-detail-card">
        <div className="pm-detail-header">
          <Truck size={20} />
          <div>
            <h3>Delivery & Install</h3>
            <p>Schedule and track the final delivery.</p>
          </div>
          <div className="pm-header-badges">
            {deliveryStatus === 'installed' && (
              <span className="pm-detail-badge pm-badge-paid">
                <CheckCircle2 size={11} style={{ verticalAlign: -2, marginRight: 3 }} />
                Project Complete
              </span>
            )}
            <span className={`pm-detail-badge pm-deliv-${deliveryStatus}`}>
              {deliveryStatus.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          </div>
        </div>

        <div className="pm-detail-body">
          <div className="cw-stack">
            <div className="cw-facts">
              <div className="cw-fact">
                <span className="cw-fact-label">Status</span>
                <span className="cw-fact-value cw-fact-strong">
                  {deliveryStatus.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              </div>
              <div className="cw-fact">
                <span className="cw-fact-label">Delivery date</span>
                <span className="cw-fact-value">
                  {deliveryDate
                    ? new Date(deliveryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—'}
                </span>
              </div>
            </div>

            <div className="cw-form-row">
              <span className="cw-form-label">Status</span>
              <select className="pm-select" value={deliveryStatus} onChange={(e) => setDeliveryStatus(e.target.value as any)}>
                <option value="scheduled">Scheduled</option>
                <option value="in-transit">In Transit</option>
                <option value="delivered">Delivered</option>
                <option value="installed">Installed</option>
              </select>
            </div>
            <div className="cw-form-row">
              <span className="cw-form-label">Delivery date</span>
              <input type="date" className="pm-input" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
            </div>
            <div className="cw-form-stack">
              <span className="cw-form-label">Completion notes</span>
              <textarea className="pm-textarea" value={completionNotes} onChange={(e) => setCompletionNotes(e.target.value)} placeholder="Delivery and installation notes..." rows={3} />
            </div>

            <button
              type="button"
              className="cw-body-save"
              onClick={() => handleSaveStepDetails({
                deliveryDate: deliveryDate ? Timestamp.fromDate(new Date(deliveryDate)) : null,
                deliveryStatus,
                completionNotes,
              })}
              disabled={saving}
            >
              <Save size={12} /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSelectedStepDetail = () => {
    switch (selectedStep) {
      case 'design-fee': return renderStepDesignFee();
      case '3d-model': return renderStep3DModel();
      case 'quote': return renderStepQuote();
      case 'quote-signed': return renderStepQuoteSigned();
      case '2d-plans': return renderStep2DPlans();
      case 'in-production': return renderStepProduction();
      case 'delivery': return renderStepDelivery();
      default: return null;
    }
  };

  const renderAgentWorkspace = (placement: 'column' | 'full') => {
    if (!lead || !leadId) return null;
    return (
      <div className={placement === 'column' ? 'cw-docs-col' : undefined} ref={workspaceDocsRef}>
        <LeadAgentWorkspace
          lead={lead}
          leadId={leadId}
          relatedDeals={relatedDeals}
          activeDealId={activeDealId}
          onOpenPdf={(doc, opts) =>
            setPdfViewerDoc({ url: doc.url, title: doc.name, documentId: doc.id ?? doc.url, annotationId: opts?.annotationId, pageNumber: opts?.pageNumber })
          }
          onOpenImage={(doc, opts) =>
            setImageViewerDoc({
              url: doc.url,
              title: doc.name,
              documentId: doc.id ?? doc.url,
              mimeType: doc.mimeType,
              annotationId: opts?.annotationId,
            })
          }
          onOpenCsv={(doc, opts) =>
            setCsvViewerDoc({ url: doc.url, title: doc.name, documentId: doc.id ?? doc.url, annotationId: opts?.annotationId })
          }
          onOpenPdfFromComment={(url, title, documentId, opts) =>
            setPdfViewerDoc({ url, title, documentId, annotationId: opts?.annotationId, pageNumber: opts?.pageNumber })
          }
          onOpenImageFromComment={(url, title, documentId, opts) =>
            setImageViewerDoc({ url, title, documentId, mimeType: opts?.mimeType, annotationId: opts?.annotationId })
          }
          onOpenCsvFromComment={(url, title, documentId, opts) =>
            setCsvViewerDoc({ url, title, documentId, annotationId: opts?.annotationId })
          }
          expandTeamNotes={openTeamNotesFromUrl}
          highlightCommentId={teamNoteCommentId || null}
          onHighlightConsumed={clearTeamNoteDeepLink}
        />
      </div>
    );
  };

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════

  if (loading) {
    return (
      <div className="ops-hub cw-hub">
        <AppHeader />
        <div className="ops-hub-layout">
          <div className="ops-hub-main">
            <div className="cw-loading">
              <RefreshCw className="spin" size={24} />
              <p>Loading client workspace...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="ops-hub cw-hub">
        <AppHeader />
        <div className="ops-hub-layout">
          <div className="ops-hub-main">
            <div className="cw-error">
              <h2>Lead Not Found</h2>
              <p>The lead you're looking for doesn't exist.</p>
              <button onClick={() => navigate(-1)} className="cw-header-btn">
                <ArrowLeft size={16} /> Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ops-hub cw-hub">
      <AppHeader />
      <div className="ops-hub-layout">
        {/* ═══ Project Sidebar ═══ */}
        <ClientWorkspaceSidebar
          visibleDeals={portalVisibleDeals}
          archivedDeals={portalArchivedDeals}
          activeDealId={activeDealId}
          getDealStage={getSidebarDealStage}
          getDealDisplay={getDealDisplay}
          onSelectDeal={(dealId, opts) => selectProjectTab(dealId, opts)}
          onRenameDeal={deal => handleStartRename(deal)}
          onTransferDeal={deal => setTransferDealTarget(deal)}
          onToggleVisibility={handleTogglePortalVisibility}
          onDeleteDeal={handleDeleteProject}
          onAddProject={() => setShowAddProjectModal(true)}
          portal={portal ? { slug: portal.slug, accessPin: portal.accessPin } : null}
          copied={copied}
          onCopyLink={handleCopyLink}
          onCreatePortal={handleCreatePortal}
          saving={saving}
          onNavigateDocuments={scrollToDocuments}
          onNavigateNotes={focusTeamNotes}
          onNavigateComms={focusLeadComms}
          onOpenAgent={openAgentPanel}
        />

        <div className="ops-hub-main cw-main">
          {/* ═══ Compact Client Header ═══ */}
          <ClientWorkspaceHeader
            lead={lead}
            onStatusChange={async status => {
              if (!lead.id) return;
              await updateLead(lead.id, { status });
            }}
            editingName={editingLeadName}
            editNameValue={editFullName}
            onEditNameChange={setEditFullName}
            onStartEditName={handleEditLeadName}
            onSaveName={handleSaveLeadName}
            onCancelEditName={handleCancelEditLeadName}
            onEditClientInfo={handleEditClientInfo}
            mapsLoaded={mapsLoaded}
            onBack={() => navigate(-1)}
            onHelp={() => setShowClientWorkspaceHelp(true)}
            onOpenProjectHub={
              timberProjectForActiveDeal?.id
                ? () => navigate(`/project-hub/${timberProjectForActiveDeal.id}`)
                : undefined
            }
          />

          <div className="cw-scroll">
            {!portal && (
              <div className="cw-no-portal">
                <Lock size={20} />
                <div>
                  <strong>No Client Portal</strong>
                  <p>Create a portal to share project progress with {lead.firstName}.</p>
                </div>
                <button onClick={handleCreatePortal} className="cw-portal-create-btn" disabled={saving}>
                  <Plus size={16} /> {saving ? 'Creating...' : 'Create Portal'}
                </button>
              </div>
            )}

            {portal && relatedDeals.length === 0 && (
              <div className="cw-empty-projects">
                <Box size={32} />
                <p className="cw-empty-projects-title">No projects yet</p>
                <p>Use "Add Project" in the sidebar to get started.</p>
              </div>
            )}

            {portal && relatedDeals.length > 0 && (
              <>
                {showAssignNumberBanner && activeDeal && (
                  <div className="pm-assign-number-banner">
                    <AlertTriangle size={18} />
                    <div className="pm-assign-number-banner-text">
                      <strong>
                        {timberProjectForActiveDeal?.phase === 'contracted' || activeDeal?.outcome === 'won'
                          ? 'Under contract'
                          : 'Design fee received'}
                      </strong>
                      <span>Assign a project number to activate operational tracking (QuickBooks, Project Hub, files).</span>
                    </div>
                    <button
                      type="button"
                      className="pm-assign-number-banner-btn"
                      onClick={() => handleStartRename(activeDeal, { assignNumber: true })}
                    >
                      Assign project number
                    </button>
                  </div>
                )}

                {/* ═══ Stage Rail — engine-driven, project label always visible ═══ */}
                <WorkspaceStageRail
                  steps={TIMELINE_STEPS}
                  statuses={stepStatuses}
                  selectedStep={selectedStep}
                  onSelectStep={setSelectedStep}
                  activeProjectLabel={activeProjectLabel}
                  displayStage={effectiveCurrentStage}
                  manualOverride={!!stageSignals.manualOverride}
                />

                {/* ═══ Stage module: suggestions + advance/retreat, with the timeline ═══ */}
                <WorkspaceStageFooter
                  storedStage={storedPortalStage}
                  selectedStep={selectedStep}
                  saving={saving}
                  advanceBlockedReason={getAdvanceBlockedReason()}
                  onAdvance={handleStageChange}
                  onRetreat={handleRevertStage}
                  actions={nextBestActions}
                  onAction={handleNextBestAction}
                />

                {/* ═══ Step detail + right rail ═══ */}
                <div className="cw-content-row">
                  <div className="cw-step-col" data-tour="cw-step-detail">
                  {renderSelectedStepDetail()}

                  {(() => {
                    const submissionSteps: PortalStage[] = ['design-fee', '3d-model', 'quote'];
                    if (!submissionSteps.includes(selectedStep as PortalStage)) return null;
                    const allNotes = portalNotes.filter(n => submissionSteps.includes(n.step));
                    const allUploads = (portal.clientUploads || []).filter(u => submissionSteps.includes(u.step));
                    if (allNotes.length === 0 && allUploads.length === 0) return null;
                    return (
                      <div className="pm-detail-card pm-submissions-card">
                        <div className="pm-detail-header">
                          <StickyNote size={20} />
                          <div>
                            <h3>Client Submissions</h3>
                            <p>Project description & inspiration from the client portal.</p>
                          </div>
                        </div>
                        <div className="pm-detail-body">
                          {allNotes.length > 0 && (
                            <div className="pm-detail-section">
                              <label className="pm-detail-label">
                                <MessageSquare size={14} /> Client Notes ({allNotes.length})
                              </label>
                              <div className="pm-feedback-list">
                                {allNotes.map(note => (
                                  <div key={note.id} className={`pm-feedback-item pm-feedback-${note.authorType}`}>
                                    <div className="pm-feedback-avatar">
                                      <User size={12} />
                                    </div>
                                    <div className="pm-feedback-content">
                                      <div className="pm-feedback-meta">
                                        <span className="pm-feedback-author">{note.authorName}</span>
                                        <span className="pm-feedback-badge">{note.authorType}</span>
                                        <span className="pm-feedback-time">
                                          {new Date(note.createdAt).toLocaleDateString()}
                                        </span>
                                      </div>
                                      <p className="pm-feedback-message">{note.message}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {allUploads.length > 0 && (
                            <div className="pm-detail-section">
                              <label className="pm-detail-label">
                                <Upload size={14} /> Uploaded Files ({allUploads.length})
                              </label>
                              <div className="pm-submissions-gallery">
                                {allUploads.map(file => {
                                  const isImage = file.type?.startsWith('image/');
                                  return isImage ? (
                                    <a key={file.id} href={file.url} target="_blank" rel="noopener noreferrer" className="pm-submissions-thumb">
                                      <img src={file.url} alt={file.name} />
                                    </a>
                                  ) : (
                                    <a key={file.id} href={file.url} target="_blank" rel="noopener noreferrer" className="pm-client-upload-item">
                                      <Download size={14} />
                                      <span>{file.name}</span>
                                    </a>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  </div>

                  {/* ═══ Right rail: activity ═══ */}
                  <aside className="cw-right-rail" data-tour="cw-right-rail">
                    <PortalBillingSection
                      dealId={activeDealId || portal.dealId}
                      fallbackBaseTotal={quoteDisplayTotal}
                    />
                    <PortalActivityTimeline
                      portalId={portal.id!}
                      projects={portal.projects}
                      additionalDealIds={portal.dealId ? [portal.dealId] : undefined}
                      clientDisplayName={portal.clientName || (lead ? `${lead.firstName} ${lead.lastName}`.trim() : '') || undefined}
                      stakeholderInvite={{
                        links: stakeholderLinks,
                        newName: newStakeholderName,
                        onNewNameChange: setNewStakeholderName,
                        busy: stakeholderBusy,
                        copiedLinkId: copiedStakeholderId,
                        onCreate: handleCreateStakeholderLink,
                        onRevoke: handleRevokeStakeholderLink,
                        onCopyUrl: handleCopyStakeholderUrl,
                      }}
                    />
                  </aside>

                  {/* Documents, team notes, and comms share the portal column. */}
                  {renderAgentWorkspace('column')}
                </div>
              </>
            )}

            {/* No portal column yet — documents still fill the main area. */}
            {!(portal && relatedDeals.length > 0) && renderAgentWorkspace('full')}
          </div>
        </div>
      </div>

        {/* ═══ Rename Project Wizard ═══ */}
        {renamingDealId && lead && (() => {
          const targetDeal = relatedDeals.find(d => d.id === renamingDealId);
          if (!targetDeal) return null;
          const fallbackTitle = parseLegacyTitle(targetDeal.title);
          const initialLabel =
            targetDeal.clientLabel ||
            defaultClientLabelFromLead(lead) ||
            fallbackTitle.label ||
            (targetDeal.contactName?.split(' ').pop() ?? '');
          const initialDescriptor =
            targetDeal.descriptor ||
            fallbackTitle.descriptor ||
            defaultDescriptorFromProjectType(targetDeal.projectType) ||
            (targetDeal.projectName ?? '');
          return (
            <NameProjectWizard
              mode="edit"
              deferProjectNumber={!targetDeal.projectNumber && !renamingAssignNumber}
              assignProjectNumberOnSubmit={renamingAssignNumber && !targetDeal.projectNumber}
              allowAssignProjectNumber={!targetDeal.projectNumber}
              assignNumberHint={
                timberProjectForActiveDeal?.phase === 'contracted' || targetDeal.outcome === 'won'
                  ? 'Under contract jobs need a project number for QuickBooks, files, and the client portal.'
                  : undefined
              }
              title={renamingAssignNumber && !targetDeal.projectNumber ? 'Assign project number' : undefined}
              confirmLabel={renamingAssignNumber && !targetDeal.projectNumber ? 'Assign number' : undefined}
              initialValue={{
                projectNumber: targetDeal.projectNumber || '',
                clientLabel: initialLabel,
                descriptor: initialDescriptor,
              }}
              warningMessage="Rename will update Project P&L, file listings, calendar, and the client portal label. The Project Number, public proposal URL, and QuickBooks Job stay the same."
              onClose={handleCancelRename}
              onConfirm={async (next) => {
                await handleConfirmRename(renamingDealId, next);
              }}
            />
          );
        })()}

        {/* ═══ Add Project Modal ═══ */}
        {showAddProjectModal && (
          <div className="pm-modal-overlay" onClick={() => setShowAddProjectModal(false)}>
            <div className="pm-modal-content pm-add-project-modal" onClick={e => e.stopPropagation()}>
              <div className="pm-modal-header">
                <h3>Add New Project</h3>
                <button className="pm-modal-close" onClick={() => setShowAddProjectModal(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className="pm-modal-body">
                <label className="pm-defer-number-toggle">
                  <input
                    type="checkbox"
                    checked={deferProjectNumber}
                    onChange={e => setDeferProjectNumber(e.target.checked)}
                    disabled={addingProject}
                  />
                  <span>
                    <strong>Assign project number later</strong>
                    <span className="pm-defer-number-hint">
                      Use for quotes before design fee payment. A project number will be assigned after the design fee is received.
                    </span>
                  </span>
                </label>
                <div className="pm-form-field">
                  <label className="pm-detail-label">Project name</label>
                  <NameProjectFields
                    mode="create"
                    value={newProjectNaming}
                    onChange={setNewProjectNaming}
                    onError={setNewProjectError}
                    disabled={addingProject}
                    deferProjectNumber={deferProjectNumber}
                  />
                </div>
                {newProjectError && (
                  <div className="pm-form-error">{newProjectError}</div>
                )}
              </div>
              <div className="pm-modal-footer">
                <button className="pm-btn-secondary" onClick={() => setShowAddProjectModal(false)}>Cancel</button>
                <button
                  className="pm-create-portal-btn"
                  onClick={handleAddProject}
                  disabled={
                    addingProject ||
                    (!deferProjectNumber && !isProjectNumber(newProjectNaming.projectNumber)) ||
                    !newProjectNaming.clientLabel.trim() ||
                    !newProjectNaming.descriptor.trim()
                  }
                >
                  {addingProject ? <><Loader2 size={14} className="spin" /> Creating...</> : <><Plus size={14} /> Create Project</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {transferDealTarget?.id && user?.id && (
          <TransferDealToLeadModal
            deal={transferDealTarget}
            userId={user.id}
            onClose={() => setTransferDealTarget(null)}
            onSuccess={({ targetLeadId }) => {
              const dealId = transferDealTarget.id;
              setTransferDealTarget(null);
              navigate(`/client/${targetLeadId}${dealId ? `?deal=${dealId}` : ''}`);
            }}
          />
        )}

        {/* ═══ Edit Client Info Modal (was the bottom "Client Details" card) ═══ */}
        {editingClientInfo && (
          <div className="pm-modal-overlay" onClick={() => setEditingClientInfo(false)}>
            <div className="pm-modal-content cw-edit-client-modal" onClick={e => e.stopPropagation()}>
              <div className="pm-modal-header">
                <h3><User size={16} /> Edit Client Details</h3>
                <button className="pm-modal-close" onClick={() => setEditingClientInfo(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className="pm-modal-body">
                <div className="pm-edit-grid">
                  <div className="pm-edit-field">
                    <Mail size={14} />
                    <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="Email" />
                  </div>
                  <div className="pm-edit-field">
                    <Phone size={14} />
                    <input type="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="Phone" />
                  </div>
                  <div className="pm-edit-field">
                    <Building2 size={14} />
                    <input type="text" value={editCompany} onChange={(e) => setEditCompany(e.target.value)} placeholder="Company" />
                  </div>
                  <div className="pm-edit-field">
                    <MapPin size={14} />
                    <input type="text" value={editLocation} onChange={(e) => setEditLocation(e.target.value)} placeholder="Location" />
                  </div>
                  <div className="pm-edit-field">
                    <FileText size={14} />
                    <input type="text" value={editProjectType} onChange={(e) => setEditProjectType(e.target.value)} placeholder="Project Type" />
                  </div>
                  <div className="pm-edit-field">
                    <DollarSign size={14} />
                    <input type="number" value={editBudget} onChange={(e) => setEditBudget(e.target.value)} placeholder="Budget" />
                  </div>
                </div>
              </div>
              <div className="pm-modal-footer">
                <button className="pm-btn-secondary" onClick={() => setEditingClientInfo(false)}>Cancel</button>
                <button className="pm-create-portal-btn" onClick={handleSaveClientInfo} disabled={savingClientInfo}>
                  <Save size={14} /> {savingClientInfo ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

      {pdfViewerDoc && leadId && lead && (
        <PdfViewerModal
          url={pdfViewerDoc.url}
          title={pdfViewerDoc.title}
          documentId={pdfViewerDoc.documentId}
          leadId={leadId}
          leadName={`${lead.firstName} ${lead.lastName}`.trim() || undefined}
          dealId={activeDealId || undefined}
          projectId={timberProjectForActiveDeal?.id}
          initialAnnotationId={pdfViewerDoc.annotationId}
          initialPageNumber={pdfViewerDoc.pageNumber}
          onClose={() => setPdfViewerDoc(null)}
        />
      )}

      {imageViewerDoc && leadId && lead && (
        <ImageViewerModal
          url={imageViewerDoc.url}
          title={imageViewerDoc.title}
          documentId={imageViewerDoc.documentId}
          leadId={leadId}
          leadName={`${lead.firstName} ${lead.lastName}`.trim() || undefined}
          dealId={activeDealId || undefined}
          projectId={timberProjectForActiveDeal?.id}
          mimeType={imageViewerDoc.mimeType}
          initialAnnotationId={imageViewerDoc.annotationId}
          onClose={() => setImageViewerDoc(null)}
        />
      )}

      {csvViewerDoc && leadId && lead && (
        <CsvViewerModal
          url={csvViewerDoc.url}
          title={csvViewerDoc.title}
          documentId={csvViewerDoc.documentId}
          leadId={leadId}
          leadName={`${lead.firstName} ${lead.lastName}`.trim() || undefined}
          dealId={activeDealId || undefined}
          projectId={timberProjectForActiveDeal?.id}
          initialAnnotationId={csvViewerDoc.annotationId}
          onClose={() => setCsvViewerDoc(null)}
        />
      )}

      {showClientWorkspaceHelp && (
        <ClientWorkspaceHelpModal
          onClose={() => setShowClientWorkspaceHelp(false)}
          onTakeTour={() => {
            setShowClientWorkspaceHelp(false);
            startClientWorkspaceTour();
          }}
        />
      )}

    </div>
  );
};

export default ClientPortalManagement;
